/**
 * The career runtime (M2 plan §6 M2-22, spec §3.3): the one place that holds the `Career`, the market, the save
 * slots and the space world together, headless (no DOM, no three.js), so the whole loop is tested in Node.
 *
 * - **Events → commands.** `tick()` runs after every sim tick: it drains the space event ring (`oreCollected` →
 *   `addOre`, `partSecured` → `addComponent` from `salvage.securedLog` plus recovery progress, `groupDefeated` →
 *   contract progress or `repoDefeated`, `docked` → the dock flow, `playerDied` → the death flow), checks the next
 *   lease due and contract deadline with one comparison, runs the death rebuild `deathRespawnSeconds` after a death
 *   and autosaves every `autosaveSeconds` in space. The no-event path allocates nothing (D-014); commands (the
 *   career layer) allocate and run only on events, due times and UI actions.
 * - **After every command:** the mirror (cargo, tool stats, collections, owned), the derived player ship def and
 *   weapons (overwritten in place on a refit: `createShipViews` sized its pools at boot, so no def is ever
 *   appended), and the effects as `SEV.career` notices (HUD toasts, audio) and `UiNotice`s (station screens).
 * - **UI.** The runtime owns the `UiActions` and the zustand store: a UI action runs its command at once and
 *   republishes at once; tick-driven changes mark the snapshot dirty and the career feature republishes at most
 *   `career.json snapshotHz` times a second.
 * - **Flow.** Holds (`title`, `docked`, `moment`) stop the sim without the pause menu: docked at a station, on the
 *   title screen, and while the ownership moment shows in space. `begin` (the first gesture) is called
 *   synchronously at the start of every session action, inside the click that started it.
 * - **Saves.** Records hold the Career and the per-system world deltas (`worlds[systemId]`); a load always resumes
 *   docked at `career.lastDockedStation`. The space RNG is re-seeded from the career seed on new and load.
 *
 * - **Strategic market** (M4 plan I3, seam S-M2-2): while the Reach's economy runs (`deps.strategicMarket`), ware
 *   quotes come from the station's real stock, and `sellWare`/`buyWare`/`sellAllCargo` validate against the Career,
 *   commit through the worker first and book the executed unit price when it answers (one trade in flight at a
 *   time; a refusal leaves the Career untouched and shows as `lastError`). Ore and component sales are reported.
 * - **Goods** (economy plan SY-2c): `buyGood`/`sellGood`/`quoteGood` trade the goods block (`cargo.parts`) on the
 *   same quote → commit → book path, only under the strategic economy (M2's own market has no goods), and only as
 *   `career.json goods.tradeEnabled` allows (`goodTradeAllowed`: off, only the relay parts can be bought). Sell
 *   all never sells goods: they are sold deliberately.
 * - **Break-down** (economy plan SY-3): `breakDown` takes a hold component apart into goods in the hold
 *   (`career/breakdown.ts`), refused while `goods.tradeEnabled` is off. It never reports a component sale: nothing
 *   reaches the station's market.
 * - **Recondition** (economy plan SY-5b, D8): `recondition` brings a Serviceable hold component up to Intact for goods
 *   from the hold (`career/recondition.ts`; no credit fee), at outfitting stations, refused while
 *   `goods.tradeEnabled` is off. Nothing reaches the station's market.
 * - **Salvaged modules** (economy plan SY-5c over SY-5a, D8): `installComponent`, `fitSalvaged` and `sellSalvaged`
 *   pass through to `career/fitting.ts` at outfitting stations; not gated by the goods flag (no goods move).
 * - **Craft commands** (M8 plan MC-0b): `UiActions.craft` runs a lane-K career command (berth, fabrication, study,
 *   Rush spares, decode) through the handler its feature registered on `craft/hook.ts`, with a `CraftCtx` built here
 *   from the runtime's own pieces: `commit`, the trade lock (`inFlight`) and the goods commit path (`commitGoods`
 *   books through a handler's `book`). This is the only hook edit to this file in M8.
 * - **Data cores** (economy plan SY-7): `deliverCore` hands a generic hold core to a `salvage.json coreDelivery`
 *   faction's market station for standing, no credits (`career/cores.ts`), scaled by the region of the system the
 *   delivery is made in (`CareerReachLink.region`; ×1 without the Reach). Refused under the M7 S14 retaliation
 *   refusals like a component sale; not gated by the goods flag (no goods move); nothing reaches a station market.
 *
 * - **Price log** (M8 plan TR-1b, D-487): while the Reach runs (`deps.priceFeed`), the pilot's `priceLog` records the
 *   local system's best prices on system entry (its first live view) and on each dock. Covered systems (the current
 *   one ∪ systems with a built player-corp station) refresh once per online strategic day (`ReachClock.day`, on the
 *   client's clock event, never per frame): at most one `fetchSystemView` per covered system per day.
 * - **Consequence lines** (M8 plan TR-5): a ware or goods sale under the strategic economy keeps the station's live
 *   view from before the commit when a line there is starved on what was sold; each later live view of that station
 *   is compared with it (`trade-intel/consequence.ts`), and a restarted line gives exactly one toast line (a
 *   `UiNotice`; no Wire post). The watch ends on that line, a sale at another station, a launch or a restore.
 *
 * The game clock is `CareerMirror.gameTime` (the space stages advance it; it stops while held). It is folded into the
 * Career whenever a command runs or a save is written.
 */
import { stationIndexOf, moduleStats } from '../data/m2'
import { WARE_IDS, wareIndex as m2WareIndex, type DockingConfig, type M2Data, type ModuleId, type ModuleKind, type SlotId, type StationDef, type StationId, type StationService, type WareId } from '../data/m2Schemas'
import type { ShipDef } from '../data/schemas'
import { logger } from '../core/logger'
import type { Quat, Vec3 } from '../core/math'
import { corridorRadius } from '../docking/corridor'
import {
  acceptContract as acceptContractCmd,
  abandonContract as abandonContractCmd,
  bookWorksHandIn,
  expireContracts,
  handInContract as handInContractCmd,
  isWorksContract,
  worksHandIn,
  worksLive,
  type WorksHandIn,
} from '../contracts/commands'
import { onGroupDefeated as contractGroupDefeated, onPartSecured as contractPartSecured } from '../contracts/progress'
import { quote, setWareQuoteSource, type Quote } from '../economy/quote'
import { buyWare as buyWareCmd, sellAllCargo as sellAllCargoCmd, sellComponent as sellComponentCmd, sellWare as sellWareCmd, type TradeValue } from '../economy/trade'
import { m2QuoteSource, type MarketQuote, type StrategicMarket } from '../strategic-link/market'
import { defaultGalaxySeed, freshReachState } from '../save/reach'
import { buildRecord, type SaveRecord } from '../save/record'
import type { ReachState } from '../reach/types'
import type { RegionId } from '../galaxy/types'
import { exportSlot, importSave as importSaveCmd } from '../save/exportImport'
import { createSaveSlots, slotLabel, type SaveSlots } from '../save/slots'
import { openSaveStorage, type OpenedStorage } from '../save/storage'
import { noteBoardedShipLost } from '../fleet-link/loss'
import { liveParkedLRecord, settleCarried } from '../fleet-link/parkedL'
import { flownEntry } from '../fleet-link/hangar'
import { partOrigin } from './salvageOrigin'
import type { ShipLimitsOf } from '../fleet-link/switch'
import { attachShip, detachShip, isDetached, respawnShipAt, type Sim } from '../sim'
import {
  clearNav,
  clearToolInputEdges,
  COLLECTIONS_CODE,
  emptyWorldDeltas,
  TOOL,
  type CareerMirror,
  type MarketState,
  type SpaceWorld,
  type StationRuntime,
  type WorldDeltas,
} from '../space/contracts'
import { CAREER_NOTICE, ENCOUNTER_KIND, firstReadableSpace, emitSpaceEvent, SEV, SEV_MASK, type CareerNoticeCode, type SpaceEventRing } from '../space/events'
import { UI_CONFIG } from '../ui/config'
import { raidersThanks } from '../ui/models/seeds'
import type {
  CmdOutcome,
  ConfirmRequest,
  DeathReport,
  ManualSlotId,
  SaveExport,
  SaveSlotId,
  SaveSlotInfo,
  StationTab,
  UiActions,
  UiNotice,
  UiStoreApi,
} from '../ui/contracts'
import { MANUAL_SLOTS } from '../ui/contracts'
import { createUiStore } from '../ui/store'
import { marketRefusal } from '../story/refusals'
import { Hull, Life, Rot, Ship, Weapons } from '../world/components'
import { dockModeOf } from '../data/bands'
import { moorConfig } from '../docking/moorConfig'
import { type TenderLine, createTenderState, leaveRing, queueTenderLine, sendTender as sendTenderRun, stepTender, takeDelivered, TENDER_PHASE, tenderGate, tenderProgress } from '../space/tender'
import { berthsOnDock } from './berth'
import { addComponent, addOre, cargoValue, contentsMassT, contentsValue } from './cargo'
import { applyDeath } from './death'
import { deriveShipDef, deriveToolStats, deriveWeapons } from './derive'
import {
  buyModule as buyModuleCmd,
  fitModule as fitModuleCmd,
  fitSalvaged as fitSalvagedCmd,
  installComponent as installComponentCmd,
  sellModule as sellModuleCmd,
  sellSalvaged as sellSalvagedCmd,
} from './fitting'
import { isRepoPaused, payArrears as payArrearsCmd, payExtra as payExtraCmd, processDue, repoDefeated, setInsurance as setInsuranceCmd } from './lease'
import { newCareer } from './newCareer'
import { BULK_WARE_MASK, holdMultInto, isProcessorDef } from './hullFit'
import { processCareer, processingConfig, processingPending } from './processing'
import { addGoods, cargoFreeT, cargoUsedT, contentsLoaded, EPSILON, GOOD_NAMES, goodTradeAllowed, removeGoods, withGameTime } from './ops'
import { goodT, isCareerOnlyGoodId, isGoodId, type GoodId, type StrategicGoodId } from './parts'
import { breakDownComponent } from './breakdown'
import { deliverCore as deliverCoreCmd } from './cores'
import { reconditionComponent } from './recondition'
import { formatCredits, formatTonnes, insufficientFunds, type CareerEffect, type CmdResult, type CommandError } from './result'
import { bookServiceDraw, drawShare, planRearm, planRepair, shortLine, type ServiceDraw, type ServicePlan } from './services'
import type { CargoComponent, CargoContents, Career, ContractState, GoodOrWare } from './types'
import { recordPrices } from '../trade-intel/priceLog'
import { consequenceLines, starvedOn } from '../trade-intel/consequence'
import type { CommandResult, StationView, StrategicCommand, SystemEconomyView } from '../strategic/types'
import { craftRegistry, type CraftBook, type CraftCommandBase, type CraftCtx, type CraftFill, type CraftMarket } from '../craft/hook'

// ---------------------------------------------------------------------------------------------
// Holds, texts and the player-ship setup
// ---------------------------------------------------------------------------------------------

/** Hold reasons the career puts on the game loop (`GameContext.holds`). */
export const HOLD = {
  /** The title screen is up (no career yet). */
  title: 'title',
  /** Docked at a station (the station screens). */
  docked: 'docked',
  /** The ownership moment shows while in space. */
  moment: 'moment',
  /** The flight dashboard is open with Settings › Dashboard time = Pause (dashboard spec §1.3, D-TBD-3). */
  dashboard: 'dashboard',
  /** A save is loading (restored, its Reach entry still running): no ticks and no station input until it docks. */
  loading: 'loading',
} as const

/** Player-facing lines the runtime raises (notices, confirms and refusals); `{name}` fields are filled in. */
export const CAREER_TEXTS = {
  finance: 'Tethys Crew Finance',
  leasePaid: 'Lease payment received: {amount} cr. Thank you for flying Tethys.',
  paymentMissed: 'Payment missed: a late fee of {amount} cr was added and your rate went up.',
  collections: 'Your account is in collections. Clear your arrears at the Lease Office.',
  collectionsCleared: 'Your account is back in good standing.',
  repoWarning: 'Repossession ordered: a recovery crew will come for the Kestrel.',
  ownership: 'Final payment received. The Kestrel is yours.',
  contractCompleted: 'Contract complete: {amount} cr paid.',
  contractFailed: 'Contract failed: {amount} standing with the issuer.',
  standing: 'Standing with {faction}: {delta} (now {value}).',
  dataCore: 'Data core logged.',
  debt: '{amount} cr added to your insurer debt.',
  premium: 'Insurance premium charged: {amount} cr.',
  saved: 'Saved to {slot}.',
  imported: 'Imported into {slot}.',
  assetsFailed: 'Station models failed to load ({id}). Reload the page to try again.',
  tradeSettling: 'The last trade is still settling.',
  tenderUnavailable: 'No tender here: trades settle at once.',
  tenderBusy: 'Nothing to send, or the tender is still out.',
  worksTreasuryShort: 'Faction treasury short: the site cannot pay for the {good} now. Your cargo stays in the hold.',
  worksPriceMoved: 'The works price moved: the site pays a new rate for {good} today. Check the contract and try again.',
  worksNotNeeded: 'The site no longer needs {good}. Your cargo stays in the hold.',
  priceMovedSell: 'The price moved: {station} now pays {unit} cr/t for {ware}. Check the price and try again.',
  priceMovedBuy: 'The price moved: {station} now charges {unit} cr/t for {ware}. Check the price and try again.',
  noRoom: '{station} has room for only {qty} t of {ware}.',
  soldWhatFits: 'Sold {sold} t of {ware} at {station}; {kept} t kept (no more room).',
  noStock: '{station} has only {qty} t of {ware} for sale.',
  nothingSells: 'Nothing in the hold sells at {station}.',
  marketDown: 'The market is not answering. Try again in a moment.',
  goodsClosed: 'Goods trading is not open yet.',
  craftUnknown: 'That is not available yet.',
  craftFailed: 'That could not be done. Try again in a moment.',
  craftOutsideTrade: 'A craft trade must run inside its trade lock.',
  rawWareNeedsBook: 'A raw ware trade for a craft command needs its own booking.',
  noGoodsMarket: '{station} has no goods market.',
  notActive: 'No career is running.',
  notDocked: 'You are not docked at a station.',
  assetsLoading: 'Station models are still loading.',
  loading: 'Still loading. Try again in a moment.',
  noService: '{station} has no {service}.',
  noSave: 'There is no saved career to continue.',
  confirmNew: {
    title: 'Start a new career?',
    message: 'Your manual saves stay in their slots. The new career autosaves over the oldest autosaves as you fly.',
    confirmLabel: 'New career',
    cancelLabel: 'Cancel',
  },
  confirmOverwrite: {
    title: 'Overwrite {slot}?',
    message: '{slot} already holds a save. It will be replaced.',
    confirmLabel: 'Overwrite',
    cancelLabel: 'Cancel',
  },
  confirmBad: {
    title: 'Overwrite {slot}?',
    message: '{slot} holds a save this build cannot read. Export it first to keep a copy.',
    confirmLabel: 'Overwrite',
    cancelLabel: 'Cancel',
  },
} as const

/** Player-facing service names for the "no service" refusal. */
const SERVICE_NAMES: Readonly<Record<StationService, string>> = {
  market: 'market',
  outfitting: 'outfitting service',
  repair: 'repair yard',
  lease: 'Lease Office',
  contracts: 'contract board',
}

/** `text` with every `{key}` replaced. */
export function fillText(text: string, fields: Readonly<Record<string, string | number>>): string {
  return text.replace(/\{(\w+)\}/g, (all, key: string) => (key in fields ? String(fields[key]) : all))
}

/** The player ship's derived-stat wiring (plan §6 M2-22): captured once when the career feature starts. */
export interface PlayerShipSetup {
  /**
   * Index of the player's ship def slot in `sim.defs` (the start ship's, the Kestrel's; only the player flies it).
   * M6 P24b: the slot holds the derived def of the ship flown now (`career.ship.defId`), keeping the slot's `id`.
   */
  readonly defIndex: number
  /** A pristine copy of the start def, before any module changes it. */
  readonly pristine: ShipDef
  /** M6 P24b: a pristine copy of every sim ship def by id (the first def of each id), for the flown ship's def. */
  readonly pristineById: ReadonlyMap<string, ShipDef>
  /** Indices of the player's own PDC and pulse-laser weapon defs in `sim.data.weapons.weapons`. */
  readonly weaponIndex: { readonly pdc: number; readonly laser: number }
}

/**
 * Captures the pristine player def and appends the two player weapon defs (copies of the PDC and pulse laser) to
 * `sim.data.weapons.weapons`. Call once, during the career feature's `init`, before the fx, HUD and audio features
 * build their per-weapon tables (they init later). Never appends a ship def.
 */
export function setupPlayerShip(sim: Sim, data: M2Data): PlayerShipSetup {
  const defIndex = data.career.start.shipDefIndex
  const def = sim.defs[defIndex] as ShipDef | undefined
  if (def === undefined) throw new Error(`career: the start ship (def ${defIndex}) is not in the sim`)
  const pristine = structuredClone(def)
  const pristineById = new Map<string, ShipDef>()
  for (const d of sim.defs) if (!pristineById.has(d.id)) pristineById.set(d.id, d === def ? pristine : structuredClone(d))
  const weapons = sim.data.weapons.weapons
  const derived = deriveWeapons(sim.data.weapons, data.career.start.fitted, data)
  const pdc = weapons.length
  weapons.push(derived.pdc)
  const laser = weapons.length
  weapons.push(derived.laser)
  return { defIndex, pristine, pristineById, weaponIndex: { pdc, laser } }
}

/**
 * Where a death wreck may lie (D-083: death wrecks never block a bay): `pos` moved out of every docking corridor
 * (sideways, to the corridor radius plus `clearance`) and then out of every station's clearance sphere (radially, to
 * the station radius plus `autopilot.stationMargin` plus `clearance`). A player killed in a bay mouth would otherwise
 * leave a persistent wreck that stops every later dock there. Writes `pos` in place; true when it moved.
 */
export function clearOfStations(stations: readonly StationRuntime[], cfg: Pick<DockingConfig, 'corridor' | 'autopilot'>, clearance: number, pos: Vec3): boolean {
  let moved = false
  for (const s of stations) {
    const bay = s.bay
    if (bay !== null) {
      const rx = pos.x - bay.mouth.x
      const ry = pos.y - bay.mouth.y
      const rz = pos.z - bay.mouth.z
      const along = rx * bay.out.x + ry * bay.out.y + rz * bay.out.z
      if (along >= -bay.depth && along <= cfg.corridor.length + clearance) {
        let lx = rx - bay.out.x * along
        let ly = ry - bay.out.y * along
        let lz = rz - bay.out.z * along
        let lateral = Math.hypot(lx, ly, lz)
        const need = corridorRadius(bay, cfg) + clearance
        if (lateral < need) {
          if (lateral < EPSILON) {
            lx = bay.right.x
            ly = bay.right.y
            lz = bay.right.z
            lateral = 1
          }
          const k = need / lateral
          pos.x = bay.mouth.x + bay.out.x * along + lx * k
          pos.y = bay.mouth.y + bay.out.y * along + ly * k
          pos.z = bay.mouth.z + bay.out.z * along + lz * k
          moved = true
        }
      }
    }
    const dx = pos.x - s.pos.x
    const dy = pos.y - s.pos.y
    const dz = pos.z - s.pos.z
    const d = Math.hypot(dx, dy, dz)
    const need = s.radius + cfg.autopilot.stationMargin + clearance
    if (s.radius > 0 && d < need) {
      const ux = d < EPSILON ? (bay?.out.x ?? 1) : dx / d
      const uy = d < EPSILON ? (bay?.out.y ?? 0) : dy / d
      const uz = d < EPSILON ? (bay?.out.z ?? 0) : dz / d
      pos.x = s.pos.x + ux * need
      pos.y = s.pos.y + uy * need
      pos.z = s.pos.z + uz * need
      moved = true
    }
  }
  return moved
}

// ---------------------------------------------------------------------------------------------
// The tick (the hot path: allocation-free unless an event, a due time or a timer fires)
// ---------------------------------------------------------------------------------------------

/** Cold handlers the tick calls (closures bound once when the runtime is built). */
export interface CareerTickHandlers {
  ore(ware: number, tonnes: number): void
  part(slot: number): void
  group(group: number, kind: number): void
  docked(station: number): void
  died(x: number, y: number, z: number): void
  /** A lease due time or a contract deadline came. */
  due(): void
  /** The death wait is over: rebuild. */
  rebuild(): void
  /** The in-space autosave interval ran out. */
  autosave(): void
  /** M10 P5a12d: the processor booking is due (`nextProcessAt`). Absent = no processing. */
  process?(): void
}

/** What the per-tick path reads and writes; mutated in place. */
export interface CareerTickState {
  readonly ring: SpaceEventRing
  readonly mirror: CareerMirror
  readonly sim: Sim
  readonly on: CareerTickHandlers
  /** A career is running. */
  active: boolean
  /** Next space event to read. */
  cursor: number
  /** Earliest lease due time or contract deadline (game time). */
  nextCheckAt: number
  /** Game time of the death rebuild, or -1. */
  rebuildAt: number
  /** Docked station index, or -1 in space. */
  docked: number
  /** Game time of the next in-space autosave. */
  nextAutosaveAt: number
  /** `career.lease.repoPausedUntil`, for the mirror's `repoPaused` each tick. */
  repoPausedUntil: number
  /**
   * M10 P5a12d: game time of the next processor booking (`processing.json stepS`), or +Infinity when nothing
   * processes (the mechanic off, or no raw bulk ore on a processor hull). Absent = +Infinity.
   */
  nextProcessAt?: number
}

/** Drains the space events the career reacts to (the player's ore and parts, groups, docking, death). */
export function drainCareerEvents(t: CareerTickState): void {
  const ring = t.ring
  const p = t.sim.playerEid
  for (let i = firstReadableSpace(ring, t.cursor); i < ring.head; i++) {
    const s = i & SEV_MASK
    const type = ring.type[s]
    if (type === SEV.oreCollected) {
      if (ring.eid[s] === p) t.on.ore(ring.a[s], ring.f[s])
    } else if (type === SEV.partSecured) {
      if (ring.eid[s] === p) t.on.part(ring.c[s])
    } else if (type === SEV.groupDefeated) {
      t.on.group(ring.a[s], ring.b[s])
    } else if (type === SEV.docked) {
      if (ring.eid[s] === p) t.on.docked(ring.a[s])
    } else if (type === SEV.playerDied) {
      if (ring.a[s] === p) t.on.died(ring.x[s], ring.y[s], ring.z[s])
    }
    // Handlers may emit career notices into the same ring; the loop reads past them harmlessly.
  }
  t.cursor = ring.head
}

/** One career tick (the career feature's `afterTick`). */
export function careerTick(t: CareerTickState): void {
  if (!t.active) {
    t.cursor = t.ring.head
    return
  }
  drainCareerEvents(t)
  const now = t.mirror.gameTime
  t.mirror.repoPaused = t.repoPausedUntil > now
  if (now >= t.nextCheckAt) t.on.due()
  if (t.rebuildAt >= 0 && now >= t.rebuildAt) t.on.rebuild()
  if (t.docked < 0 && now >= t.nextAutosaveAt) t.on.autosave()
  // M10 P5a12d: a number compare per tick; the booking (cold) runs every stepS while a processor has raw ore.
  if (t.nextProcessAt !== undefined && now >= t.nextProcessAt) t.on.process?.()
}

// ---------------------------------------------------------------------------------------------
// The runtime
// ---------------------------------------------------------------------------------------------

export interface CareerRuntimeDeps {
  space: SpaceWorld
  data: M2Data
  ship: PlayerShipSetup
  /** The game loop's hold reasons (`GameContext.holds`). */
  holds: Set<string>
  /** Starts the game (the first gesture; idempotent). Called synchronously at the start of every session action. */
  begin: () => void
  /** Station notices kept on screen (oldest dropped first). */
  maxNotices: number
  /** Opens the save storage. Default: IndexedDB → localStorage → memory (`openSaveStorage`). */
  openStorage?: () => Promise<OpenedStorage>
  /** Wall clock for save times. */
  now?: () => Date
  /** A new career's seed (uint32). */
  randomSeed?: () => number
  /** Additive (M3 R22k): the Reach side of the career (the M2 bridge). Absent: the record's Reach block is carried. */
  reach?: CareerReachLink
  /**
   * Additive (M4 plan I3): the strategic market while the Reach's economy runs, read lazily (the strategic service is
   * provided after the career feature). Null or absent: M2's own prices and trades.
   */
  strategicMarket?: () => StrategicMarket | null
  /**
   * Additive (M8 plan TR-1b): what the price log records from while the Reach's economy runs, read lazily like
   * `strategicMarket` (the `StrategicClient` satisfies it). Null or absent: nothing is recorded.
   */
  priceFeed?: () => PriceFeed | null
  /** R3-1 sweep: a strategic site id → its career station id (`m2IdOfSite` over the live layout). Absent: the slug. */
  stationIdOf?: (siteId: string) => StationId
}

/** TR-1b: the strategic client as the price log reads it (`StrategicClient` satisfies it structurally). */
export interface PriceFeed {
  /** The watched system's view (the local one unless a map watches another). */
  readonly systemView: SystemEconomyView | null
  /** The local system (null while between systems). */
  readonly manifest: { readonly systemId: string } | null
  onSystemView(fn: (view: SystemEconomyView) => void): () => void
  onClock(fn: (clock: { readonly day: number }) => void): () => void
  /** One system's live view by id. */
  fetchSystemView(systemId: string): Promise<SystemEconomyView>
  /** The player corp's stations (`station` null while building). */
  playerStationViews(): Promise<readonly { readonly systemId: string; readonly station: unknown }[]>
  /**
   * M8 SNK-6b2: the worker command channel a works hand-in sends `worksDeliver` through (`StrategicClient.command`).
   * Absent: works hand-ins are refused as "market down".
   */
  command?(cmd: StrategicCommand): Promise<CommandResult>
}

/** The Reach side of a career (M3 plan R22k; `src/bridge/m2/saves.ts` implements it). */
export interface CareerReachLink {
  /** A snapshot of the live Reach state, written into every save. */
  state(): ReachState
  /**
   * Applies a record's Reach block (null: a new career) and enters the system of `career.lastDockedStation`;
   * resolves when the system is entered, and the career docks there after. `savedAt`: the loaded
   * record's (part of its strategic companion's key, M7 S41); absent for a new career.
   */
  enter(block: ReachState | null, career: Career, savedAt?: string): Promise<void>
  /** A career started docked (new or loaded): the one-time stand-in import runs here. */
  entered(): void
  /** False while the Reach moves the dead player to the last docked station's system: the death rebuild waits. */
  respawnReady(): boolean
  /**
   * Additive (economy plan SY-7): the galaxy region of system `systemId` (`M2Data.stations.systemId`; M2's home id
   * is the Reach's home system), or null when unknown. Absent: every region reads as unknown (core delivery ×1).
   */
  region?(systemId: string): RegionId | null
}

/** A dev command's hook (M2-23 `applyDevCommand`): a pure `Career → Career` change applied like any command. */
export type DevCommand = (career: Career) => Career

export interface CareerRuntime {
  readonly ui: UiStoreApi
  readonly actions: UiActions
  /** The current Career (a fresh placeholder before a career starts). */
  career(): Career
  market(): MarketState
  /** A career is running (new, continued, loaded or imported). */
  readonly active: boolean
  /** Docked station index, or -1 in space. */
  readonly docked: number
  /** The snapshot changed since the last `publish`. */
  readonly dirty: boolean
  /** Storage is open and the slot list is in the store. */
  readonly ready: Promise<void>
  /** The per-tick state (the hot path). */
  readonly tickState: CareerTickState
  /** One tick (after every sim tick). */
  tick(): void
  /** Pushes the snapshot (career, market, clock, docked station) to the store. */
  publish(): void
  /** Shows the title screen (hold `title`). */
  showTitle(): void
  /** The home system's world deltas from the runtimes and the market. */
  worldDeltas(): WorldDeltas
  /** A save record of right now (folds the clock and the ship's hull and torpedoes into the Career). */
  buildSave(): SaveRecord
  /** Replaces the Career and the world with a record (no screen change; `enterCareer` follows). */
  restore(record: SaveRecord): void
  /** Starts the restored career docked at its last station: hold, station screen, begin. */
  enterCareer(): void
  /** Writes a rolling autosave now. */
  autosave(): Promise<CmdOutcome>
  /** Deferred station and prop models: progress 0..1, and whether all have loaded. */
  setAssets(progress: number, ready: boolean): void
  /** Raises a station notice. */
  notify(kind: UiNotice['kind'], text: string, speaker?: string): void
  applyDevCommand(name: string, cmd: DevCommand): CmdOutcome
  /**
   * Economy plan SY-2c (for SY-2e's Parts tab and TR-4): the strategic quote for `qty` units of good `good` at
   * `stationId` (a career or strategic station id; docking not required), or null when no strategic market runs, the
   * station is unknown or doesn't trade the good on `side`, or `goods.tradeEnabled` doesn't allow it.
   */
  quoteGood(stationId: string, good: GoodId, qty: number, side: 'buy' | 'sell'): MarketQuote | null
  /**
   * SY-2c: buys `qty` units of good `good` at `stationId` through the strategic market (a transfer out of the
   * station's stock). Resolves when the worker answered: `{ ok: true }` once the Career is booked, else the
   * refusal (the Career is unchanged). With the flag off only the three relay parts can be bought.
   */
  buyGood(stationId: string, good: GoodId, qty: number): Promise<CmdOutcome>
  /** SY-2c: sells `qty` units of good `good` at `stationId` the same way; refused while the flag is off. */
  sellGood(stationId: string, good: GoodId, qty: number): Promise<CmdOutcome>
}

const OK: CmdOutcome = Object.freeze({ ok: true }) as CmdOutcome
const ZERO_VEL: Readonly<Vec3> = Object.freeze({ x: 0, y: 0, z: 0 })

function refusal(code: CommandError['code'], message: string): CmdOutcome {
  return { ok: false, error: { code, message } }
}

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err)
}

function defaultSeed(): number {
  const buf = new Uint32Array(1)
  globalThis.crypto.getRandomValues(buf)
  return buf[0]
}

/** A death wreck spawns only when the hold had something (goods count, SY-2b). */
function hasCargo(contents: CargoContents): boolean {
  return contentsLoaded(contents)
}

function signed(n: number): string {
  return n > 0 ? `+${n}` : String(n)
}

/** A goods venue (SY-2c): the station id the market resolves, its name for messages and the player's standing there. */
interface GoodVenue {
  id: string
  name: string
  standing: number
}

type Confirmed = { kind: 'new' } | { kind: 'import'; text: string; slot: ManualSlotId } | { kind: 'save'; slot: ManualSlotId; record: SaveRecord }

export function createCareerRuntime(deps: CareerRuntimeDeps): CareerRuntime {
  const { space, data, ship, holds } = deps
  const sim = space.sim
  const mirror = space.mirror
  const ring = space.events
  /** The current system's stations: read live, since the Reach replaces `data.stations` on every system switch (M3 R22e). */
  const stationsNow = (): M2Data['stations']['stations'] => data.stations.stations
  const systemId = data.stations.systemId
  const saveCfg = data.career.save
  const wallClock = deps.now ?? ((): Date => new Date())
  const randomSeed = deps.randomSeed ?? defaultSeed
  const openStorage = deps.openStorage ?? ((): Promise<OpenedStorage> => openSaveStorage(saveCfg))
  const weapons = sim.data.weapons.weapons

  let career: Career = newCareer(data, 0)
  /** The Reach block of the loaded record, carried into saves when no link is wired (null: a fresh one). */
  let reachBlock: ReachState | null = null
  let market: MarketState = {}
  let lastFitted: Career['ship']['fitted'] | null = null
  /** M6 P24b: the ship def id the player slot holds now (the start def at boot). */
  let lastDefId: string = ship.pristine.id
  /** M9 CH-9a: the flaw bitmask the player slot was derived with (0 = none, the lease ship and every bought ship). */
  let lastFlaws = 0
  let dirty = false
  let assetsReady = false
  /** A save is loading: restored, its Reach entry not yet docked (the load hold; `requireActive` refuses). */
  let loading = false
  let noticeSeq = 0
  let confirmSeq = 0
  let pending: { id: string; action: Confirmed } | null = null
  const deathPos: Vec3 = { x: 0, y: 0, z: 0 }
  const deathRot: Quat = { x: 0, y: 0, z: 0, w: 1 }

  // ---- The strategic market (M4 plan I3) -------------------------------------------------------

  const marketSource = deps.strategicMarket
  let watchedMarket: StrategicMarket | null = null
  let tradeInFlight = false
  // M10 P5a8a: the moored tender run; `settling` holds a landed run's lines, settled one at a time (a strategic
  // trade settles through the worker, `tradeInFlight`).
  const tenderCfg = moorConfig().tender
  const tender = createTenderState()
  let settling: TenderLine[] = []
  let tenderTimer: ReturnType<typeof setInterval> | null = null
  /** The strategic market, or null; the first time a market shows up its row changes start republishing the screens. */
  function strategic(): StrategicMarket | null {
    const m = marketSource?.() ?? null
    if (m !== null && m !== watchedMarket) {
      watchedMarket = m
      m.onChange(() => {
        dirty = true
      })
    }
    return m
  }
  if (marketSource !== undefined) setWareQuoteSource(m2QuoteSource(strategic))

  // ---- The price log (M8 plan TR-1b) -----------------------------------------------------------

  let feed: PriceFeed | null = null
  let offFeed: (() => void)[] = []
  /** The local system recorded on entry (null: none yet this career). */
  let enteredSystem: string | null = null
  /** The last online strategic day the covered systems were refreshed on (−1: none yet this career). */
  let coveredDay = -1
  /** Covered system id → the strategic day it was last fetched on (the per-day dedupe). */
  const fetchedDay = new Map<string, number>()
  /** Bumped on every restore: a fetch that answers for an older career is dropped. */
  let priceEpoch = 0

  /** The local system's live view, or null (none yet, or a map watches another system). */
  function localView(f: PriceFeed): SystemEconomyView | null {
    const v = f.systemView
    const local = f.manifest?.systemId ?? null
    return v !== null && local !== null && v.systemId === local ? v : null
  }

  /**
   * MC-c6 Mk III geography (D-TBD, K3 follow-up): the docked system as `buyModule` and `fab.start` see it, the strategic
   * client's live view of the local system. Undefined (the any-station rule) when there is none: no strategic client
   * (M2's own career, or before the client is up), between systems, or a map watching another system.
   */
  function geographyView(): SystemEconomyView | undefined {
    const f = deps.priceFeed?.() ?? null
    return f === null ? undefined : (localView(f) ?? undefined)
  }

  /** Picks up the strategic client once it exists (or a new one), subscribing to its views and clock. */
  function attachFeed(): PriceFeed | null {
    const f = deps.priceFeed?.() ?? null
    if (f === feed) return f
    for (const off of offFeed) off()
    offFeed = []
    feed = f
    if (f !== null) {
      offFeed = [
        f.onSystemView(() => {
          onLocalView(f)
          checkConsequence(f)
        }),
        f.onClock((c) => onOnlineDay(f, c.day)),
      ]
    }
    return f
  }

  /** System entry: the first live view of a new local system is recorded. */
  function onLocalView(f: PriceFeed): void {
    if (!tickState.active || f !== feed) return
    const v = localView(f)
    if (v === null || v.systemId === enteredSystem) return
    enteredSystem = v.systemId
    commit(recordPrices(folded(), v, mirror.gameTime), [])
  }

  /** Once per online strategic day: the current system from its live view, the corp-station systems fetched once. */
  function onOnlineDay(f: PriceFeed, day: number): void {
    if (!tickState.active || f !== feed || day === coveredDay) return
    coveredDay = day
    const epoch = priceEpoch
    const v = localView(f)
    if (v !== null) {
      fetchedDay.set(v.systemId, day)
      commit(recordPrices(folded(), v, mirror.gameTime), [])
    }
    f.playerStationViews().then(
      (own) => {
        for (const st of own) {
          if (st.station === null || fetchedDay.get(st.systemId) === day) continue
          fetchedDay.set(st.systemId, day)
          f.fetchSystemView(st.systemId).then(
            (view) => {
              if (!tickState.active || f !== feed || epoch !== priceEpoch) return
              commit(recordPrices(folded(), view, mirror.gameTime), [])
            },
            (err: unknown) => logger.warn(`price log: system view ${st.systemId} failed`, err),
          )
        }
      },
      (err: unknown) => logger.warn('price log: player stations unavailable', err),
    )
  }

  /** A dock: the local system's live prices go into the Career the dock commits. */
  function withDockPrices(c: Career): Career {
    const f = attachFeed()
    const v = f === null ? null : localView(f)
    return v === null ? c : recordPrices(c, v, mirror.gameTime)
  }

  // ---- Consequence lines (M8 plan TR-5) --------------------------------------------------------

  /** The station a sale may restart a line at: its view from before the first such sale, and the wares sold since. */
  interface ConsequenceWatch {
    readonly before: StationView
    readonly wares: readonly string[]
  }
  /** Ends on the restart's toast, a sale at another station, a launch or a restore. */
  let watch: ConsequenceWatch | null = null

  /** The local live view of strategic station `stationId` (a career or strategic id through `m`), or null. */
  function liveStation(m: StrategicMarket, stationId: string): StationView | null {
    const id = m.resolve(stationId)
    // Read, not attached: the subscription is made on entry and dock (`attachFeed`); a view now is all a sale needs.
    const f = deps.priceFeed?.() ?? null
    const v = f === null || id === null ? null : localView(f)
    return v?.stations.find((s) => s.id === id) ?? null
  }

  /**
   * A sale of `ware` committed at the station `before` shows (taken before the commit): a sale at the watched station
   * adds its ware; elsewhere it replaces the watch, kept only when a line there is starved on the ware. Then the
   * current view is checked at once (the sale may already show).
   */
  function watchSale(before: StationView | null, ware: string): void {
    if (before === null) return
    if (watch !== null && watch.before.id === before.id) {
      if (!watch.wares.includes(ware)) watch = { before: watch.before, wares: [...watch.wares, ware] }
    } else {
      watch = starvedOn(before, [ware]) ? { before, wares: [ware] } : null
    }
    if (feed !== null) checkConsequence(feed)
  }

  /** On a live view: a watched line that restarted gives its one toast line and ends the watch. */
  function checkConsequence(f: PriceFeed): void {
    const w = watch
    if (w === null || !tickState.active || f !== feed) return
    const after = localView(f)?.stations.find((s) => s.id === w.before.id)
    if (after === undefined) return
    const lines = consequenceLines(w.before, after, w.wares)
    if (lines.length === 0) return
    watch = null
    for (const text of lines) notify('success', text)
  }

  // ---- Storage -------------------------------------------------------------------------------

  let slotsPromise: Promise<SaveSlots> | null = null
  function storage(): Promise<SaveSlots> {
    slotsPromise ??= openStorage().then((opened) => {
      ui.setState({ storage: opened.kind })
      return createSaveSlots({
        storage: opened.storage,
        maxBytes: saveCfg.maxBytes,
        manualSlots: saveCfg.manualSlots,
        autoSlots: saveCfg.autoSlots,
      })
    })
    return slotsPromise
  }

  async function refreshSaves(): Promise<SaveSlotInfo[]> {
    const list = await (await storage()).listSlots()
    ui.setState({ saves: list })
    return list
  }

  // ---- Small helpers -------------------------------------------------------------------------

  const livePlayer = (): number => {
    const p = sim.playerEid
    return p >= 0 && Life.alive[p] === 1 ? p : -1
  }

  const folded = (): Career => (career.gameTime === mirror.gameTime ? career : withGameTime(career, mirror.gameTime))

  /** M6 P24b: full hull and torpedo rounds of a ship def by id (pristine), for the lease-ship fallback on death. */
  const shipLimitsOf: ShipLimitsOf = (id) => {
    const d = ship.pristineById.get(id)
    return d === undefined ? null : { hull: d.hull, torpedoRounds: d.torpedoRounds }
  }
  const stationKnown = (id: string): boolean => stationIndexOf(data, id) >= 0

  const stationIndex = (id: StationId): number => {
    const i = stationIndexOf(data, id)
    if (i >= 0) return i
    logger.warn(`career: unknown station "${id}", using the start station`)
    return Math.max(0, stationIndexOf(data, data.career.start.station))
  }

  function emitCareer(code: CareerNoticeCode, amount: number): void {
    emitSpaceEvent(ring, SEV.career, sim.playerEid, code, 0, 0, 0, 0, 0, amount, 0)
  }

  function notify(kind: UiNotice['kind'], text: string, speaker?: string): void {
    const notice: UiNotice = speaker === undefined ? { id: ++noticeSeq, kind, text, at: mirror.gameTime } : { id: ++noticeSeq, kind, text, speaker, at: mirror.gameTime }
    const list = [...ui.getState().notices, notice]
    ui.setState({ notices: list.length > deps.maxNotices ? list.slice(list.length - deps.maxNotices) : list })
  }

  function confirm(action: Confirmed, texts: Omit<ConfirmRequest, 'id'>): void {
    const id = `${action.kind}:${++confirmSeq}`
    pending = { id, action }
    ui.setState({ confirm: { id, ...texts } })
  }

  function confirmTexts(t: Readonly<Omit<ConfirmRequest, 'id'>>, slot: SaveSlotId): Omit<ConfirmRequest, 'id'> {
    const label = slotLabel(slot)
    return {
      title: fillText(t.title, { slot: label }),
      message: fillText(t.message, { slot: label }),
      confirmLabel: t.confirmLabel,
      cancelLabel: t.cancelLabel,
    }
  }

  // ---- Ship, mirror and effects --------------------------------------------------------------

  /**
   * Refit: the derived def and weapons overwrite the live ones in place (never appended). M6 P24b: the base is the
   * flown ship's own def (`career.ship.defId`; the start def for an old save or an unknown id). The player slot keeps
   * its `id`, so an id lookup still finds the AI ships' defs, and the AI's defs are never touched. M9 CH-9a: `flaws`
   * is the flown hangar entry's bitmask (`flownEntry`; 0 for the lease ship), so a boarded claimed hull flies flawed.
   */
  function applyFit(defId: string, fitted: Career['ship']['fitted'], flaws: number): void {
    const slot = sim.defs[ship.defIndex]
    const derived = deriveShipDef(ship.pristineById.get(defId) ?? ship.pristine, fitted, data, ship.weaponIndex, flaws)
    derived.id = ship.pristine.id
    for (const key of Object.keys(slot)) if (!(key in derived)) Reflect.deleteProperty(slot, key)
    Object.assign(slot, derived)
    const w = deriveWeapons(sim.data.weapons, fitted, data)
    Object.assign(weapons[ship.weaponIndex.pdc], w.pdc)
    Object.assign(weapons[ship.weaponIndex.laser], w.laser)
    Object.assign(mirror.tools, deriveToolStats(fitted, data))
  }

  function nextCheck(c: Career): number {
    let at = c.lease.nextDueAt
    for (const k of c.contracts.active) if (k.deadlineAt < at) at = k.deadlineAt
    return at
  }

  function syncMirror(): void {
    const c = career
    const fitted = c.ship.fitted
    const defId = c.ship.defId
    const flaws = flownEntry(c)?.flaws ?? 0
    if (fitted !== lastFitted || defId !== lastDefId || flaws !== lastFlaws) {
      applyFit(defId, fitted, flaws)
      lastFitted = fitted
      if (defId !== lastDefId || flaws !== lastFlaws) {
        lastDefId = defId
        lastFlaws = flaws
        rebuildPlayerBody()
      }
    }
    mirror.cargoCapacityT = c.cargo.capacityT
    mirror.cargoUsedT = cargoUsedT(c)
    if (mirror.holdMult !== undefined) holdMultInto(defId, mirror.holdMult)
    mirror.maxPartMassT = moduleStats(data, 'cargo', fitted.cargo).maxPartMassT
    mirror.cargoValue = cargoValue(c, data)
    mirror.collections = COLLECTIONS_CODE[c.lease.collections]
    mirror.repoPaused = isRepoPaused(c, mirror.gameTime)
    mirror.owned = c.lease.owned
    mirror.playerDefIndex = ship.defIndex
    tickState.nextCheckAt = nextCheck(c)
    tickState.repoPausedUntil = c.lease.repoPausedUntil
    armProcessing(c)
  }

  // ---- M10 P5a12d: on-site processing (`career/processing.ts`; off in shipped data until W6) -------------------

  const processCfg = processingConfig()
  const isProcessor = (defId: string): boolean => isProcessorDef(defId)
  /** Game time of the last processor booking. */
  let processedAt = 0

  /** Arms the processor booking when a processor hull has raw bulk ore aboard, else disarms it (+Infinity). */
  function armProcessing(c: Career): void {
    if (!processingPending(c, processCfg, isProcessor, BULK_WARE_MASK)) {
      tickState.nextProcessAt = Number.POSITIVE_INFINITY
      return
    }
    const now = mirror.gameTime
    const at = tickState.nextProcessAt ?? Number.POSITIVE_INFINITY
    if (at === Number.POSITIVE_INFINITY || processedAt > now || at > now + processCfg.stepS) {
      processedAt = now
      tickState.nextProcessAt = now + processCfg.stepS
    }
  }

  /** The booking: the game time since the last one, processed on every processor hull aboard or in the hangar. */
  function onProcess(): void {
    const now = mirror.gameTime
    const next = processCareer(career, now - processedAt, processCfg, isProcessor, BULK_WARE_MASK)
    processedAt = now
    tickState.nextProcessAt = now + processCfg.stepS
    if (next !== career) commit(next, [])
    else armProcessing(career)
  }

  /**
   * M6 P24b: the flown ship changed (boarding, a load): the live player's Rapier body is rebuilt from the slot's new
   * def (mass, collider radius) and its hull and torpedoes come from the Career. A dead player respawns with it.
   */
  function rebuildPlayerBody(): void {
    const p = livePlayer()
    if (p < 0) return
    if (!isDetached(sim, p)) {
      detachShip(sim, p)
      attachShip(sim, p)
    }
    shipToSim()
  }

  /** The live player's hull and torpedoes into the Career (after flying; the sim is the truth in space). */
  function foldShip(c: Career): Career {
    const p = livePlayer()
    if (p < 0) return c
    const max = sim.defs[ship.defIndex].hull
    const hull = Math.min(max, Math.max(0, Hull.value[p]))
    const torpedoes = Weapons.torpedoes[p]
    if (hull === c.ship.hull && torpedoes === c.ship.torpedoes) return c
    return { ...c, ship: { ...c.ship, hull, torpedoes } }
  }

  /** The Career's hull and torpedoes onto the live player (after repair, rearm, a load). */
  function shipToSim(): void {
    const p = livePlayer()
    if (p < 0) return
    Hull.value[p] = career.ship.hull
    Weapons.torpedoes[p] = career.ship.torpedoes
  }

  function ownershipMoment(): void {
    if (tickState.docked < 0) holds.add(HOLD.moment)
    ui.setState({ screen: 'ownership' })
  }

  function handleEffects(effects: readonly CareerEffect[]): void {
    const T = CAREER_TEXTS
    for (const e of effects) {
      switch (e.kind) {
        case 'leasePaid':
          emitCareer(CAREER_NOTICE.leasePaid, e.amount)
          notify('success', fillText(T.leasePaid, { amount: formatCredits(e.amount) }), T.finance)
          break
        case 'paymentMissed':
          emitCareer(CAREER_NOTICE.paymentMissed, e.lateFee)
          notify('warning', fillText(T.paymentMissed, { amount: formatCredits(e.lateFee) }), T.finance)
          break
        case 'collectionsChanged':
          if (e.to === 'notice') {
            emitCareer(CAREER_NOTICE.collections, 0)
            notify('error', T.collections, T.finance)
          } else if (e.to === 'none') {
            notify('success', T.collectionsCleared, T.finance)
          }
          break
        case 'repoTriggered':
          emitCareer(CAREER_NOTICE.repoWarning, 0)
          notify('error', T.repoWarning, T.finance)
          break
        case 'ownership':
          emitCareer(CAREER_NOTICE.ownership, 0)
          notify('success', T.ownership, T.finance)
          ownershipMoment()
          break
        case 'contractCompleted':
          emitCareer(CAREER_NOTICE.contractCompleted, e.reward)
          notify('success', fillText(T.contractCompleted, { amount: formatCredits(e.reward) }))
          {
            // M7 S33 (C8): the issuing desk's thank-you for a clear-raiders job (Hale at the Hub), from stations.json.
            const thanks = raidersThanks(data, e.station, e.type)
            if (thanks !== null) notify('info', thanks.text, thanks.speaker)
          }
          break
        case 'contractFailed':
          emitCareer(CAREER_NOTICE.contractFailed, 0)
          notify('warning', fillText(T.contractFailed, { amount: signed(e.standing) }))
          space.salvage.removeContractWreck(e.contractId)
          space.encounters.cancelContractGroup(e.contractId)
          break
        case 'standingChanged':
          notify('info', fillText(T.standing, { faction: UI_CONFIG.factions[e.faction], delta: signed(e.delta), value: e.value }))
          break
        case 'dataCoreLogged':
          notify('info', T.dataCore)
          break
        case 'debtIncurred':
          notify('warning', fillText(T.debt, { amount: formatCredits(e.amount) }), T.finance)
          break
        case 'premiumCharged':
          notify('info', fillText(T.premium, { amount: formatCredits(e.amount) }), T.finance)
          break
        case 'deathFee':
          emitCareer(CAREER_NOTICE.deathFee, e.amount + e.repoFee)
          break
        case 'resultLine':
          notify('success', e.text)
          break
      }
    }
  }

  /** A command succeeded: new Career, effects, mirror; the snapshot is dirty. */
  function commit(next: Career, effects: readonly CareerEffect[]): void {
    career = next
    handleEffects(effects)
    syncMirror()
    dirty = true
  }

  function publish(): void {
    dirty = false
    const docked = tickState.docked
    ui.setState({
      career: tickState.active ? career : null,
      market: tickState.active ? market : null,
      gameTime: mirror.gameTime,
      stationId: docked >= 0 ? stationsNow()[docked].id : null,
    })
  }

  // ---- UI command plumbing -------------------------------------------------------------------

  /** A refused UI action: shown inline until the next action. */
  function refuse(error: CommandError): CmdOutcome {
    ui.setState({ lastError: error })
    return { ok: false, error }
  }

  function done(): CmdOutcome {
    ui.setState({ lastError: null })
    publish()
    return OK
  }

  function uiCareer(result: CmdResult<Career>): CmdOutcome {
    if (!result.ok) return refuse(result.error)
    commit(result.value, result.effects)
    return done()
  }

  function uiTrade(result: CmdResult<TradeValue>): CmdOutcome {
    if (!result.ok) return refuse(result.error)
    market = result.value.market
    commit(result.value.career, result.effects)
    return done()
  }

  /**
   * The one gate for every career command: a career is running and fully entered. While a save loads (`loading`:
   * restored, the Reach entry still running, `enterCareer` not landed) the session is half-entered, so nothing runs.
   */
  function requireActive(): CommandError | null {
    if (loading) return { code: 'notReady', message: CAREER_TEXTS.loading }
    return tickState.active ? null : { code: 'notReady', message: CAREER_TEXTS.notActive }
  }

  /** The docked station (optionally one offering `service`), or a refusal. */
  function dockedAt(service?: StationService): StationDef | CommandError {
    const inactive = requireActive()
    if (inactive) return inactive
    if (tickState.docked < 0) return { code: 'notReady', message: CAREER_TEXTS.notDocked }
    const station = stationsNow()[tickState.docked]
    if (service !== undefined && !station.services.includes(service)) {
      return { code: 'serviceUnavailable', message: fillText(CAREER_TEXTS.noService, { station: station.name, service: SERVICE_NAMES[service] }) }
    }
    return station
  }

  function atStation(service: StationService | undefined, run: (station: StationDef, c: Career) => CmdOutcome): CmdOutcome {
    const station = dockedAt(service)
    if ('code' in station) return refuse(station)
    return run(station, folded())
  }

  // ---- The moored tender run (M10 P5a8a, space/tender.ts) ---------------------------------------
  // With moor.json tender.enabled off (until W6) `tenderGate` is false and nothing below runs: trades settle at once.

  /** Docked at a ring: docked and the flown hull's band moors (bands.json dockMode). */
  function mooredNow(): boolean {
    return tickState.docked >= 0 && dockModeOf(sim.defs[Ship.defIndex[sim.playerEid]].sizeBand) === 'moor'
  }

  /** A ware trade through the tender: checked now (a refusal shows at once), queued for the next run. */
  function queueTenderTrade(station: StationDef, c: Career, ware: WareId, tonnes: number, side: 'sell' | 'buy'): CmdOutcome {
    const check = side === 'sell' ? sellWareCmd(c, data, market, station.id, ware, tonnes) : buyWareCmd(c, data, market, station.id, ware, tonnes)
    if (!check.ok) return refuse(check.error)
    queueTenderLine(tender, tickState.docked, { side, item: ware, tonnes })
    publishTender()
    return done()
  }

  /**
   * The tender clock: the sim (and the career tick) holds while docked, so a sent run steps on a wall-clock interval
   * of `tender.tickS` (a cold timer, one per run; never in the frame loop) until it lands and its lines settle.
   */
  function startTenderClock(): void {
    if (tenderTimer !== null) return
    tenderTimer = setInterval(tenderStep, tenderCfg.tickS * 1000)
  }

  function tenderStep(): void {
    if (tender.phase === TENDER_PHASE.running && stepTender(tender, tenderCfg.tickS)) settling = settling.concat(takeDelivered(tender))
    if (settling.length > 0 && !tradeInFlight) settleNextLine()
    if (tender.phase !== TENDER_PHASE.running && settling.length === 0 && tenderTimer !== null) {
      clearInterval(tenderTimer)
      tenderTimer = null
    }
    publishTender()
  }

  /** Settles the next landed line as today's trade at the run's station (it may still be refused). */
  function settleNextLine(): void {
    const line = settling.shift()
    const station = stationsNow()[tender.runStation]
    if (line === undefined || station === undefined) return
    const ware = line.item as WareId
    const c = folded()
    const m = strategicAt(station)
    if (m !== null) strategicTrade(m, station, c, ware, line.tonnes, line.side)
    else uiTrade(line.side === 'sell' ? sellWareCmd(c, data, market, station.id, ware, line.tonnes) : buyWareCmd(c, data, market, station.id, ware, line.tonnes))
  }

  /** The tender's store view (only ever set with the key on). */
  function publishTender(): void {
    ui.setState({
      tender: {
        queuedT: tender.queuedT,
        lines: tender.queued.length,
        running: tender.phase === TENDER_PHASE.running,
        progress: tenderProgress(tender),
        remainingS: tender.phase === TENDER_PHASE.running ? tender.durationS - tender.elapsedS : 0,
      },
    })
  }

  // ---- Strategic trades (M4 plan I3, spec §5.6: commit through the worker first) ----------------

  /** The strategic market when it prices `station`, else null (M2's own trade path). */
  function strategicAt(station: StationDef): StrategicMarket | null {
    const m = strategic()
    return m !== null && m.resolve(station.id) !== null ? m : null
  }

  function wareName(ware: WareId): string {
    const def = data.wares.wares[m2WareIndex(ware)]
    return def.unidentifiedName ?? def.name
  }

  /** The Career refusal for a strategic trade refusal (the worker's typed errors, spec §11.3). */
  function tradeRefusal(name: string, label: string, side: 'sell' | 'buy', error: string, fresh?: { qty: number; unit: number }): CommandError {
    switch (error) {
      case 'priceMoved':
        return {
          code: 'priceMoved',
          message: fillText(side === 'sell' ? CAREER_TEXTS.priceMovedSell : CAREER_TEXTS.priceMovedBuy, {
            station: name,
            ware: label,
            unit: formatCredits(Math.round(fresh?.unit ?? 0)),
          }),
        }
      case 'noRoom':
        return { code: 'notBoughtHere', message: fillText(CAREER_TEXTS.noRoom, { station: name, ware: label, qty: formatTonnes(fresh?.qty ?? 0) }) }
      case 'noStock':
        return { code: 'notSoldHere', message: fillText(CAREER_TEXTS.noStock, { station: name, ware: label, qty: formatTonnes(fresh?.qty ?? 0) }) }
      case 'notTraded':
        return side === 'sell'
          ? { code: 'notBoughtHere', message: `${name} does not buy ${label}.` }
          : { code: 'notSoldHere', message: `${name} does not sell ${label}.` }
      default:
        return { code: 'serviceUnavailable', message: CAREER_TEXTS.marketDown }
    }
  }

  /**
   * Commits `qty` t of `ware` through the worker at the locally quoted price, then books the executed unit price on
   * the Career. Resolves with the refusal, or null when the trade went through.
   *
   * PT-1 (D-803): a sale sells what fits; a partial sale keeps the rest in the hold and raises `soldWhatFits`. A sale
   * refuses `noRoom` only when nothing fits. A buy over the stock still refuses `noStock` (D-132).
   */
  async function commitWare(m: StrategicMarket, station: StationDef, ware: WareId, qty: number, side: 'sell' | 'buy'): Promise<CommandError | null> {
    const standing = career.standings[station.faction]
    const q = m.quote(station.id, ware, qty, side, standing)
    if (q === null) return tradeRefusal(station.name, wareName(ware), side, 'notTraded')
    if (side === 'sell' ? !(q.qty > 0) : q.clamped) return tradeRefusal(station.name, wareName(ware), side, side === 'sell' ? 'noRoom' : 'noStock', q)
    const before = side === 'sell' ? liveStation(m, station.id) : null
    const r = await m.commit(station.id, ware, qty, side, q.unit, standing)
    if (!r.ok) return tradeRefusal(station.name, wareName(ware), side, r.error, r.quote)
    if (!tickState.active) {
      logger.warn('strategic trade: executed after the career ended; not booked')
      return null
    }
    const c = folded()
    const booked = side === 'sell' ? sellWareCmd(c, data, market, station.id, ware, r.qty, r.unit) : buyWareCmd(c, data, market, station.id, ware, r.qty, r.unit)
    if (!booked.ok) {
      // The station already traded; the Career changed under the trade (should not happen: one trade at a time).
      logger.warn(`strategic trade: executed but not booked (${booked.error.code})`)
      return booked.error
    }
    market = booked.value.market
    commit(booked.value.career, booked.effects)
    watchSale(before, ware)
    const kept = qty - r.qty
    if (side === 'sell' && kept > EPSILON) {
      notify('info', fillText(CAREER_TEXTS.soldWhatFits, { sold: formatTonnes(r.qty), kept: formatTonnes(kept), ware: wareName(ware), station: station.name }))
    }
    return null
  }

  /**
   * Runs `work` as the one trade in flight; its refusal (if any) becomes `lastError`, success republishes. Resolves
   * with the refusal, or null when the trade went through (the ware paths ignore it; `buyGood`/`sellGood` await it).
   */
  function inFlight(work: () => Promise<CommandError | null>): Promise<CommandError | null> {
    tradeInFlight = true
    return work()
      .then(
        (error) => {
          if (error === null) done()
          else refuse(error)
          return error
        },
        (err: unknown) => {
          logger.error('strategic trade failed', err)
          const error: CommandError = { code: 'serviceUnavailable', message: CAREER_TEXTS.marketDown }
          refuse(error)
          return error
        },
      )
      .finally(() => {
        tradeInFlight = false
      })
  }

  /** A ware trade under the strategic economy: validated now against the Career, executed by the worker. */
  function strategicTrade(m: StrategicMarket, station: StationDef, c: Career, ware: WareId, tonnes: number, side: 'sell' | 'buy'): CmdOutcome {
    if (tradeInFlight) return refuse({ code: 'notReady', message: CAREER_TEXTS.tradeSettling })
    const check = side === 'sell' ? sellWareCmd(c, data, market, station.id, ware, tonnes) : buyWareCmd(c, data, market, station.id, ware, tonnes)
    if (!check.ok) return refuse(check.error)
    const held = c.cargo.wares[ware]
    const qty = side === 'sell' && tonnes > held ? held : tonnes
    void inFlight(() => commitWare(m, station, ware, qty, side))
    return done()
  }

  // ---- Station services paid in goods (M8 plan SNK-3b): plan → (quote → commit) → book -----------------------

  /**
   * Runs a planned repair or rearm. A credit (or free) plan books at once. A draw plan uses the hold first: when the
   * station doesn't stock the good (or sits at its reserve) only the hold pays, and nothing at all is refused; else
   * the station's part is quoted (`quoteDraw`), checked against the wallet, and committed through the worker in the
   * trade lock (`inFlight`). The hold and the wallet change only in the booking after the worker settled.
   */
  function runService(station: StationDef, c: Career, planned: CmdResult<ServicePlan>): CmdOutcome {
    if (!planned.ok) return refuse(planned.error)
    const plan = planned.value
    if (plan.kind === 'done') {
      commit(plan.career, plan.effects)
      shipToSim()
      return done()
    }
    const draw = plan.draw
    if (tradeInFlight) return refuse({ code: 'notReady', message: CAREER_TEXTS.tradeSettling })
    const m = strategicAt(station)
    const standing = c.standings[station.faction]
    const q = m === null || draw.fromStationT <= 0 ? null : m.quoteDraw(station.id, draw.good, draw.fromStationT, draw.sink, standing)
    if (m === null || q === null || q.qty <= EPSILON) {
      // Hold only: no station part wanted, none stocked here, or the station is at its reserve.
      const short = draw.fromStationT > 0
      const line = q === null ? draw.texts.none : shortLine(draw, drawShare(draw, 0))
      if (draw.fromHoldT <= EPSILON) return refuse({ code: 'notSoldHere', message: line })
      const booked = bookServiceDraw(c, draw, 0, 0)
      if (!booked.ok) return refuse(booked.error)
      commit(booked.value, booked.effects)
      shipToSim()
      if (short) notify('warning', line)
      return done()
    }
    if (c.wallet + EPSILON < q.total) return refuse(insufficientFunds(Math.ceil(q.total - EPSILON), c.wallet).error)
    const budget = Math.floor(c.wallet + EPSILON)
    void inFlight(() => commitServiceDraw(m, station, draw, q.qty, q.unit, standing, budget))
    return done()
  }

  /**
   * Sends the station's part of a service draw (`playerDraw`) and, once the worker settled, books the hold tonnes,
   * the executed total and the service for the share covered (`bookServiceDraw`). A refusal changes nothing.
   */
  async function commitServiceDraw(m: StrategicMarket, station: StationDef, draw: ServiceDraw, wantT: number, unit: number, standing: number, budget: number): Promise<CommandError | null> {
    const r = await m.draw(station.id, draw.good, wantT, unit, draw.sink, standing, budget)
    if (!r.ok) {
      if (r.error === 'noStock') return { code: 'notSoldHere', message: shortLine(draw, 0) }
      if (r.error === 'notTraded' || r.error === 'unknownStation') return { code: 'notSoldHere', message: draw.texts.none }
      return tradeRefusal(station.name, GOOD_NAMES[draw.good], 'buy', r.error, r.quote)
    }
    if (!tickState.active) {
      logger.warn('service draw: executed after the career ended; not booked')
      return null
    }
    const credits = Math.min(Math.round(r.total), budget)
    const booked = bookServiceDraw(folded(), draw, r.got, credits)
    if (!booked.ok) {
      logger.warn(`service draw: executed but not booked (${booked.error.code})`)
      return booked.error
    }
    commit(booked.value, booked.effects)
    shipToSim()
    const share = drawShare(draw, r.got)
    if (share < 1) notify('warning', shortLine(draw, share))
    return null
  }

  // ---- Works hand-in (M8 plan SNK-6b2, D-TBD T31): a treasury transfer priced by the worker ---------------------

  /**
   * A works hand-in: validated now against the Career and the live row (`worksHandIn`), then `worksDeliver` goes to
   * the worker in the trade lock (`inFlight`, so launch and a ship switch wait). The hold and the wallet change only
   * after the worker settled: exactly the tonnes the site accepted leave the hold, and the wallet gets exactly the
   * `paid` the treasury paid (`bookWorksHandIn`). A refusal keeps the goods and credits nothing.
   */
  function handInWorks(station: StationDef, c: Career, contract: ContractState): CmdOutcome {
    if (tradeInFlight) return refuse({ code: 'notReady', message: CAREER_TEXTS.tradeSettling })
    const feed = deps.priceFeed?.() ?? null
    if (feed === null || feed.command === undefined) return refuse({ code: 'serviceUnavailable', message: CAREER_TEXTS.marketDown })
    const plan = worksHandIn(data, c, station.id, contract.id, worksLive(feed.systemView, contract))
    if (!plan.ok) return refuse(plan.error)
    void inFlight(() => commitWorks(feed, station, contract, plan.value))
    return done()
  }

  /** The Career refusal for a `worksDeliver` refusal (each leaves the hold and the wallet alone). */
  function worksRefusal(error: string, good: StrategicGoodId): CommandError {
    const name = GOOD_NAMES[good]
    switch (error) {
      case 'treasuryShort':
        return { code: 'serviceUnavailable', message: fillText(CAREER_TEXTS.worksTreasuryShort, { good: name }) }
      case 'priceMoved':
        return { code: 'priceMoved', message: fillText(CAREER_TEXTS.worksPriceMoved, { good: name }) }
      case 'siteGone':
      case 'notSupplying':
      case 'notNeeded':
        return { code: 'contractRequirement', message: fillText(CAREER_TEXTS.worksNotNeeded, { good: name }) }
      default:
        return { code: 'serviceUnavailable', message: CAREER_TEXTS.marketDown }
    }
  }

  /**
   * Sends `worksDeliver` and, once the worker settled, books exactly what it accepted and paid. The completion's
   * `playerContractDone` (the issuer's influence) goes out from here with the treasury's actual `paid`, not the
   * board's estimate; the Charter career bridge leaves works contracts to this path.
   */
  async function commitWorks(feed: PriceFeed, station: StationDef, contract: ContractState, h: WorksHandIn): Promise<CommandError | null> {
    const contractId = contract.id
    if (feed.command === undefined) return { code: 'serviceUnavailable', message: CAREER_TEXTS.marketDown }
    const r = await feed.command({ type: 'worksDeliver', siteId: h.siteId, ware: h.good, qty: h.qty, expectedReward: h.expectedReward })
    if (!r.ok) return worksRefusal(r.error, h.good)
    if (r.works === undefined) return { code: 'serviceUnavailable', message: CAREER_TEXTS.marketDown }
    if (!tickState.active) {
      logger.warn('works hand-in: executed after the career ended; not booked')
      return null
    }
    const booked = bookWorksHandIn(data, folded(), contractId, r.works)
    if (!booked.ok) {
      logger.warn(`works hand-in: executed but not booked (${booked.error.code})`)
      return booked.error
    }
    commit(booked.value, booked.effects)
    const systemId = contract.params.systemId
    if (systemId !== undefined) {
      const paid = r.works.paid
      feed.command({ type: 'playerContractDone', factionId: station.faction, systemId, reward: paid }).then(
        (res) => {
          if (!res.ok) logger.warn(`works hand-in: playerContractDone refused (${res.error})`)
        },
        (err: unknown) => logger.warn('works hand-in: playerContractDone failed', err),
      )
    }
    return null
  }

  // ---- Goods trades (economy plan SY-2c): the ware path's quote → commit → book, on the goods block ----------

  /**
   * The venue for `stationId` (a career or strategic id): the current system's station that plays it gives the name
   * and the standing (by its faction); a strategic station with no career station shows its id at standing 0.
   */
  function goodVenue(m: StrategicMarket, stationId: string, c: Career): GoodVenue | null {
    const resolved = m.resolve(stationId)
    if (resolved === null) return null
    const def = stationsNow().find((s) => s.id === stationId) ?? stationsNow().find((s) => m.resolve(s.id) === resolved)
    return def === undefined ? { id: resolved, name: resolved, standing: 0 } : { id: def.id, name: def.name, standing: c.standings[def.faction] }
  }

  /** The goods flag's refusal, or null when `side` of `good` is allowed (`goodTradeAllowed`). */
  function goodsGate(good: GoodId, side: 'buy' | 'sell'): CommandError | null {
    return goodTradeAllowed(data.career.goods.tradeEnabled, good, side) ? null : { code: 'serviceUnavailable', message: CAREER_TEXTS.goodsClosed }
  }

  function quoteGoodAt(stationId: string, good: GoodId, qty: number, side: 'buy' | 'sell'): MarketQuote | null {
    // A career-only good (Rush spares, MC-h3) has no strategic ware: no station quotes it.
    if (isCareerOnlyGoodId(good) || goodsGate(good, side) !== null) return null
    const m = strategic()
    if (m === null) return null
    const at = goodVenue(m, stationId, career)
    return at === null ? null : m.quote(at.id, good, qty, side, at.standing)
  }

  /** Commits `qty` units of `good` through the worker at the local quote, then books the executed price (`commitWare`). */
  async function commitGood(m: StrategicMarket, at: GoodVenue, good: StrategicGoodId, qty: number, side: 'sell' | 'buy'): Promise<CommandError | null> {
    const label = GOOD_NAMES[good]
    const q = m.quote(at.id, good, qty, side, at.standing)
    if (q === null) return tradeRefusal(at.name, label, side, 'notTraded')
    if (q.clamped) return tradeRefusal(at.name, label, side, side === 'sell' ? 'noRoom' : 'noStock', q)
    const before = side === 'sell' ? liveStation(m, at.id) : null
    const r = await m.commit(at.id, good, qty, side, q.unit, at.standing)
    if (!r.ok) return tradeRefusal(at.name, label, side, r.error, r.quote)
    if (!tickState.active) {
      logger.warn('strategic goods trade: executed after the career ended; not booked')
      return null
    }
    const credits = Math.round(r.unit * r.qty)
    const booked = side === 'sell' ? removeGoods(folded(), good, r.qty, credits) : addGoods(folded(), good, r.qty, credits)
    if (!booked.ok) {
      logger.warn(`strategic goods trade: executed but not booked (${booked.error.code})`)
      return booked.error
    }
    commit(booked.value, booked.effects)
    watchSale(before, good)
    return null
  }

  /**
   * M7 S14 retaliation on goods (D-TBD Phase 1 follow-ups): a goods trade is refused at a station of the current
   * system where the ware trade or the component trade on the same side is (`marketRefusal`), so a Tethys row
   * refuses goods like wares and the parts desk's `componentsRefused` also covers the goods a break-down yields.
   * Checked before anything goes to the strategic worker, like the ware path. A strategic station with no career
   * station here (no faction known) is not refused.
   */
  function goodsRefusal(c: Career, stationId: string, side: 'buy' | 'sell'): CommandError | null {
    const def = stationsNow().find((s) => s.id === stationId)
    if (def === undefined) return null
    const r = marketRefusal(c, def, side === 'sell' ? 'sellWare' : 'buyWare') ?? marketRefusal(c, def, side === 'sell' ? 'sellComponent' : 'buyComponent')
    return r === null ? null : r.error
  }

  /**
   * A goods trade: refused at once (as `lastError`) when no career runs, the flag forbids it, the station has no
   * goods market or doesn't trade the good, the station refuses the player under Tethys retaliation, a trade is settling, or the Career can't pay, carry or deliver it; else
   * committed through the worker. Returns the refusal, or the pending trade (its refusal, or null once booked).
   */
  function goodTrade(stationId: string, good: GoodId, qty: number, side: 'buy' | 'sell'): CommandError | Promise<CommandError | null> {
    const inactive = requireActive()
    if (inactive) return inactive
    const gate = goodsGate(good, side)
    if (gate) return gate
    const m = strategic()
    const c = folded()
    const at = m === null ? null : goodVenue(m, stationId, c)
    if (m === null || at === null) {
      const name = stationsNow().find((s) => s.id === stationId)?.name ?? stationId
      return { code: 'serviceUnavailable', message: fillText(CAREER_TEXTS.noGoodsMarket, { station: name }) }
    }
    const refused = goodsRefusal(c, at.id, side)
    if (refused) return refused
    if (isCareerOnlyGoodId(good)) return tradeRefusal(at.name, GOOD_NAMES[good], side, 'notTraded')
    if (tradeInFlight) return { code: 'notReady', message: CAREER_TEXTS.tradeSettling }
    if (!(Number.isFinite(qty) && qty > 0)) return { code: 'invalidAmount', message: `Invalid quantity: ${String(qty)}.` }
    const held = goodT(c.cargo.parts, good)
    const units = side === 'sell' && qty > held ? held : qty
    const q = m.quote(at.id, good, units, side, at.standing)
    if (q === null) return tradeRefusal(at.name, GOOD_NAMES[good], side, 'notTraded')
    const check = side === 'sell' ? removeGoods(c, good, qty, Math.round(q.total)) : addGoods(c, good, qty, Math.round(q.total))
    if (!check.ok) return check.error
    return inFlight(() => commitGood(m, at, good, units, side))
  }

  /** The UI form of a goods trade (docked; sync like `buyWare`): a refusal shows at once, the trade settles later. */
  function uiGoodTrade(station: StationDef, good: GoodId, qty: number, side: 'buy' | 'sell'): CmdOutcome {
    const r = goodTrade(station.id, good, qty, side)
    if (r instanceof Promise) {
      void r
      return done()
    }
    return refuse(r)
  }

  /** The runtime form (SY-2e's Parts tab): resolves when the worker answered and the Career is booked. */
  async function runtimeGoodTrade(stationId: string, good: GoodId, qty: number, side: 'buy' | 'sell'): Promise<CmdOutcome> {
    const r = goodTrade(stationId, good, qty, side)
    if (!(r instanceof Promise)) return refuse(r)
    const error = await r
    return error === null ? OK : { ok: false, error }
  }

  // ---- Craft commands (M8 plan MC-0b): the hook's context, built from the pieces above ----------------------

  /**
   * The strategic market as a craft handler sees it. `commitGoods` is the goods path's quote → commit → book, with
   * the booking left to the handler (`book`) so a fill can go somewhere other than the hold (MC-c3b's buy-missing
   * lands in a fab order's `delivered`). `locked` is true only while this command's `ctx.inFlight` work runs.
   */
  function craftMarket(m: StrategicMarket, locked: () => boolean): CraftMarket {
    return {
      quote(stationId: string, item: GoodOrWare, qty: number, side: 'buy' | 'sell'): MarketQuote | null {
        if (isCareerOnlyGoodId(item)) return null
        if (isGoodId(item) && goodsGate(item, side) !== null) return null
        const at = goodVenue(m, stationId, career)
        return at === null ? null : m.quote(at.id, item, qty, side, at.standing)
      },
      async commitGoods(stationId: string, item: GoodOrWare, qty: number, side: 'buy' | 'sell', book?: CraftBook): Promise<CommandError | null> {
        if (!locked()) return { code: 'notReady', message: CAREER_TEXTS.craftOutsideTrade }
        const good = isGoodId(item)
        if (!good && book === undefined) return { code: 'invalidAmount', message: CAREER_TEXTS.rawWareNeedsBook }
        if (good) {
          const gate = goodsGate(item, side)
          if (gate) return gate
        }
        const c = folded()
        const at = goodVenue(m, stationId, c)
        if (at === null) {
          const name = stationsNow().find((s) => s.id === stationId)?.name ?? stationId
          return { code: 'serviceUnavailable', message: fillText(CAREER_TEXTS.noGoodsMarket, { station: name }) }
        }
        const refused = goodsRefusal(c, at.id, side)
        if (refused) return refused
        if (!(Number.isFinite(qty) && qty > 0)) return { code: 'invalidAmount', message: `Invalid quantity: ${String(qty)}.` }
        const label = good ? GOOD_NAMES[item] : wareName(item)
        if (isCareerOnlyGoodId(item)) return tradeRefusal(at.name, label, side, 'notTraded')
        const q = m.quote(at.id, item, qty, side, at.standing)
        if (q === null) return tradeRefusal(at.name, label, side, 'notTraded')
        if (q.clamped) return tradeRefusal(at.name, label, side, side === 'sell' ? 'noRoom' : 'noStock', q)
        const r = await m.commit(at.id, item, qty, side, q.unit, at.standing)
        if (!r.ok) return tradeRefusal(at.name, label, side, r.error, r.quote)
        if (!tickState.active) {
          logger.warn('craft trade: executed after the career ended; not booked')
          return null
        }
        const fill: CraftFill = { qty: r.qty, unit: r.unit, credits: Math.round(r.unit * r.qty) }
        let booked: CmdResult<Career>
        if (book !== undefined) booked = book(folded(), fill)
        else if (isGoodId(item)) booked = side === 'sell' ? removeGoods(folded(), item, fill.qty, fill.credits) : addGoods(folded(), item, fill.qty, fill.credits)
        else return { code: 'invalidAmount', message: CAREER_TEXTS.rawWareNeedsBook }
        if (!booked.ok) {
          logger.warn(`craft trade: executed but not booked (${booked.error.code})`)
          return booked.error
        }
        commit(booked.value, booked.effects)
        return null
      },
    }
  }

  /** The context for one craft command, at `station` (null in space). */
  function craftCtx(station: StationDef | null): CraftCtx {
    let locked = false
    const m = strategic()
    return {
      career: folded,
      commit(next: Career, effects: readonly CareerEffect[] = []): void {
        if (!tickState.active) {
          logger.warn('craft command: commit after the career ended; ignored')
          return
        }
        commit(next, effects)
      },
      station,
      data,
      now: mirror.gameTime,
      system: geographyView(),
      market: m === null ? null : craftMarket(m, () => locked),
      inFlight(work: () => Promise<CommandError | null>): CmdOutcome {
        if (tradeInFlight) return refuse({ code: 'notReady', message: CAREER_TEXTS.tradeSettling })
        locked = true
        void inFlight(() =>
          work().finally(() => {
            locked = false
          }),
        )
        return done()
      },
      refuse,
      done,
    }
  }

  /** `UiActions.craft`: the registered handler for `cmd.kind` runs with a fresh context; unknown kinds are refused. */
  function craftCommand(cmd: CraftCommandBase): CmdOutcome {
    const inactive = requireActive()
    if (inactive) return refuse(inactive)
    const handler = craftRegistry.handler(cmd.kind)
    if (handler === undefined) return refuse({ code: 'serviceUnavailable', message: CAREER_TEXTS.craftUnknown, detail: { kind: cmd.kind } })
    const station = tickState.docked >= 0 ? stationsNow()[tickState.docked] : null
    try {
      return handler(cmd, craftCtx(station))
    } catch (err: unknown) {
      logger.error(`craft command ${cmd.kind} failed`, err)
      return refuse({ code: 'serviceUnavailable', message: CAREER_TEXTS.craftFailed, detail: { kind: cmd.kind } })
    }
  }

  /**
   * Sell all under the strategic economy: loose components sell at once (M2's price, reported), then every ware the
   * station buys commits through the worker one after another. Refuses with `notBoughtHere` when nothing sells.
   * Goods are never sold here (SY-2c: they are sold deliberately, with `sellGood`).
   */
  function strategicSellAll(m: StrategicMarket, station: StationDef, c: Career): CmdOutcome {
    if (tradeInFlight) return refuse({ code: 'notReady', message: CAREER_TEXTS.tradeSettling })
    const standing = c.standings[station.faction]
    const wares: WareId[] = []
    for (const ware of WARE_IDS) {
      if (c.cargo.wares[ware] > EPSILON && m.quote(station.id, ware, 0, 'sell', standing) !== null) wares.push(ware)
    }
    let sold = 0
    for (const component of c.cargo.components) {
      if (component.contractId !== undefined || component.type === 'cargoPod') continue
      const r = sellComponentCmd(folded(), data, market, station.id, component.id)
      if (!r.ok) continue
      market = r.value.market
      commit(r.value.career, r.effects)
      m.reportComponentSold(station.id, component.type, component.condition)
      sold++
    }
    if (sold === 0 && wares.length === 0) return refuse({ code: 'notBoughtHere', message: fillText(CAREER_TEXTS.nothingSells, { station: station.name }) })
    if (wares.length > 0) {
      void inFlight(async () => {
        let last: CommandError | null = null
        for (const ware of wares) {
          const held = career.cargo.wares[ware]
          if (!(held > EPSILON)) continue
          const error = await commitWare(m, station, ware, held, 'sell')
          if (error !== null) last = error
        }
        return last
      })
    }
    return done()
  }

  // ---- Event handlers (the career layer: run only on events) --------------------------------

  function onOre(wareIndex: number, tonnes: number): void {
    const ware = WARE_IDS[wareIndex] as WareId | undefined
    if (ware === undefined) return
    const r = addOre(folded(), ware, tonnes)
    if (!r.ok) {
      syncMirror()
      return
    }
    commit(r.value, r.effects)
    strategic()?.reportMined(ware, tonnes)
  }

  function onPart(slot: number): void {
    const entry = space.salvage.securedLog.entries[slot] as (typeof space.salvage.securedLog.entries)[number] | undefined
    if (entry === undefined) return
    const contents = entry.contents === null ? null : structuredClone(entry.contents)
    const component: CargoComponent = {
      id: entry.key,
      type: entry.type,
      condition: Math.min(1, Math.max(0, entry.condition)),
      massT: contents === null ? entry.massT : entry.massT + contentsMassT(contents),
      value: Math.max(0, entry.value),
    }
    if (entry.mk > 0) component.mk = entry.mk
    // M8 MC-f5a: who made it and the Rush tag ride from the pool into the hold (the study route, the Rush scan line).
    if (entry.maker !== null) component.maker = entry.maker
    if (entry.rush) component.provenance = 'rush'
    if (entry.contractId !== null) component.contractId = entry.contractId
    if (contents !== null) component.contents = contents
    const before = folded()
    // A duplicate id is detected up front, never from the refusal code (`invalidAmount` also means a bad mass).
    // The sim already removed the part, so a duplicate goes in under a suffixed id instead of being lost (BUG-B1).
    if (before.cargo.components.some((x) => x.id === component.id)) component.id = `${entry.key}#${before.stats.partsSecured}`
    // M7 S37: a derelict part carries its salvage site (the Harrow core log reads it; `career/salvageOrigin.ts`).
    const r = addComponent(before, component, partOrigin(entry.key))
    if (!r.ok) {
      // The part is lost (the sim removed it first). The mirror's room pre-check should prevent this, so a
      // refusal is logged as a metric: a mirror/career free-tonnage gap shows a desync.
      logger.warn('salvage.partRefused', {
        reason: r.error.code,
        partId: component.id,
        mirrorFreeT: mirror.cargoCapacityT - mirror.cargoUsedT,
        careerFreeT: cargoFreeT(before),
      })
      syncMirror()
      return
    }
    let next: Career = { ...r.value, stats: { ...r.value.stats, partsSecured: r.value.stats.partsSecured + 1 } }
    next = contractPartSecured(next, { type: entry.type, contractId: entry.contractId })
    commit(next, r.effects)
  }

  function onGroup(group: number, kind: number): void {
    if (kind === ENCOUNTER_KIND.contract) {
      const id = space.encounters.contractOf(group)
      if (id !== null) commit(contractGroupDefeated(folded(), id), [])
    } else if (kind === ENCOUNTER_KIND.repo) {
      const r = repoDefeated(folded(), mirror.gameTime)
      commit(r.value, r.effects)
    }
  }

  function onDocked(station: number): void {
    if (station < 0 || station >= stationsNow().length) return
    if (tickState.docked === station) return
    tickState.docked = station
    holds.add(HOLD.docked)
    // Folding the clock refreshes the station's contract board (boards follow the lease cycle).
    const next = withDockPrices({ ...foldShip(folded()), lastDockedStation: stationsNow()[station].id })
    // MC-b5: berth access from the local live view (stamp, clear, relocate due berths); the dock autosave carries it.
    commit(berthsOnDock(next, data, geographyView(), mirror.gameTime, null, deps.stationIdOf), [])
    ui.setState({ screen: 'station', lastError: null })
    publish()
    void autosave()
  }

  function onDied(x: number, y: number, z: number): void {
    if (tickState.rebuildAt >= 0) return
    tickState.rebuildAt = mirror.gameTime + data.career.deathRespawnSeconds
    deathPos.x = x
    deathPos.y = y
    deathPos.z = z
    const p = sim.playerEid
    deathRot.x = Rot.x[p]
    deathRot.y = Rot.y[p]
    deathRot.z = Rot.z[p]
    deathRot.w = Rot.w[p]
  }

  function repoCrewAlive(): boolean {
    const g = space.encounters.groups
    for (let i = 0; i < g.capacity; i++) if (g.active[i] === 1 && g.kind[i] === ENCOUNTER_KIND.repo && g.alive[i] > 0) return true
    return false
  }

  /** The death rebuild: fees, the cargo into a death wreck, respawn docked at the last station. */
  function onRebuild(): void {
    // A respawn transfer to another system is still running: try again next tick (rebuildAt stays due).
    if (deps.reach !== undefined && !deps.reach.respawnReady()) return
    tickState.rebuildAt = -1
    const def = sim.defs[ship.defIndex]
    const r = applyDeath(folded(), data, { repoActive: repoCrewAlive(), shipDef: def, limitsOf: shipLimitsOf, stationKnown, carrier: liveParkedLRecord() })
    if (!r.ok) {
      logger.error(`career: death rebuild refused (${r.error.message})`)
      return
    }
    const { career: next, contents } = r.value
    const cargoVal = contentsValue(contents, data)
    if (hasCargo(contents)) {
      clearOfStations(space.stations.list, data.docking, def.colliderRadius, deathPos)
      space.salvage.spawnDeathWreck(deathPos, ZERO_VEL, deathRot, contents)
    }
    const station = stationIndex(next.lastDockedStation)
    const s = space.stations.list[station]
    const p = sim.playerEid
    if (p >= 0) respawnShipAt(sim, p, s.pos.x, s.pos.y, s.pos.z, s.rot)
    space.docking.dockAt(station)
    tickState.docked = station
    holds.add(HOLD.docked)
    const report: DeathReport = { station: stationsNow()[station].id, fee: 0, repoFee: 0, addedToPrincipal: !career.lease.owned, debtIncurred: 0, cargoValue: cargoVal }
    for (const e of r.effects) {
      if (e.kind === 'deathFee') {
        report.fee = e.amount
        report.repoFee = e.repoFee
        report.addedToPrincipal = e.addedToPrincipal
      } else if (e.kind === 'debtIncurred') {
        report.debtIncurred += e.amount
      }
    }
    // M6 (D-459 bug 5): a flown fleet ship's death gets its own wording (lost for good, or rebuilt under insurance).
    if (r.value.fleetShip !== undefined) report.fleetShip = r.value.fleetShip
    commit(next, r.effects)
    // M6 P24b: an uninsured flown fleet ship was lost outright; the fleet link frees its worker slot.
    if (r.value.lostShip !== undefined) noteBoardedShipLost(r.value.lostShip)
    // M10 P5a11b: the S in the lost L's cradle went down with it (its CARRIED_UNTIL slot, after the L's).
    if (r.value.lostCarried !== undefined) noteBoardedShipLost(r.value.lostCarried)
    shipToSim()
    ui.setState({ screen: 'death', deathReport: report, lastError: null })
    publish()
    void autosave()
  }

  function onDue(): void {
    const now = mirror.gameTime
    const due = processDue(folded(), data, now)
    const expired = expireContracts(data, due.value, now)
    commit(expired.value, [...due.effects, ...expired.effects])
  }

  function onAutosaveTimer(): void {
    tickState.nextAutosaveAt = mirror.gameTime + saveCfg.autosaveSeconds
    void autosave()
  }

  const tickState: CareerTickState = {
    ring,
    mirror,
    sim,
    on: {
      ore: onOre,
      part: onPart,
      group: onGroup,
      docked: onDocked,
      died: onDied,
      due: onDue,
      rebuild: onRebuild,
      autosave: onAutosaveTimer,
      process: onProcess,
    },
    active: false,
    cursor: ring.head,
    nextCheckAt: Number.POSITIVE_INFINITY,
    rebuildAt: -1,
    docked: -1,
    nextAutosaveAt: Number.POSITIVE_INFINITY,
    repoPausedUntil: 0,
    nextProcessAt: Number.POSITIVE_INFINITY,
  }

  // ---- Saves and sessions --------------------------------------------------------------------

  function worldDeltas(): WorldDeltas {
    return {
      rocks: space.rocks.exportDeltas(),
      salvage: space.salvage.exportDeltas(),
      markets: market,
      encounters: space.encounters.exportDeltas(),
    }
  }

  function buildSave(): SaveRecord {
    const next = foldShip(folded())
    if (next !== career) {
      career = next
      dirty = true
    }
    const reach = currentReach()
    // M10 P5a11b (C2): a reload docks the pilot at a station, so the saved Career has the S released from its L's pad
    // (the L parked at its live pose); the live Career stays carried.
    return buildRecord({ career: settleCarried(career, liveParkedLRecord()), gameTime: mirror.gameTime, worlds: { [systemId]: worldDeltas() }, reach, galaxySeed: reach.galaxySeed, now: wallClock() })
  }

  /** The Reach block for a save: the link's live state, else the carried block, else a fresh canonical Reach. */
  function currentReach(): ReachState {
    if (deps.reach !== undefined) return deps.reach.state()
    reachBlock ??= freshReachState(defaultGalaxySeed())
    return reachBlock
  }

  function restore(record: SaveRecord): void {
    const world = record.worlds[systemId] ?? emptyWorldDeltas()
    reachBlock = record.reach
    career = record.career.gameTime === record.gameTime ? record.career : withGameTime(record.career, record.gameTime)
    market = world.markets
    space.rng.state = career.seed >>> 0
    mirror.gameTime = career.gameTime
    space.rocks.importDeltas(world.rocks)
    space.salvage.importDeltas(world.salvage)
    space.encounters.importDeltas(world.encounters)
    lastFitted = null
    enteredSystem = null
    coveredDay = -1
    fetchedDay.clear()
    priceEpoch++
    watch = null
    syncMirror()
    dirty = true
  }

  /** The player alive (a load may come while it is dead) with its def's full stats. */
  function ensurePlayer(station: number): void {
    const p = sim.playerEid
    if (p < 0) throw new Error('career: the sim has no player ship')
    if (Life.alive[p] !== 1) {
      const s = space.stations.list[station]
      respawnShipAt(sim, p, s.pos.x, s.pos.y, s.pos.z, s.rot)
    }
  }

  function enterCareer(): void {
    const station = stationIndex(career.lastDockedStation)
    tickState.active = true
    tickState.rebuildAt = -1
    syncMirror()
    ensurePlayer(station)
    space.tools.setMode(TOOL.combat)
    clearNav(space.player.nav)
    clearToolInputEdges(space.input)
    space.input.scanHeld = false
    space.input.flightActive = false
    space.docking.dockAt(station)
    tickState.docked = station
    shipToSim()
    tickState.cursor = ring.head
    tickState.nextAutosaveAt = mirror.gameTime + saveCfg.autosaveSeconds
    pending = null
    holds.add(HOLD.docked)
    holds.delete(HOLD.title)
    holds.delete(HOLD.moment)
    // TR-1b: a career starts docked in the system it entered; that entry is recorded (its view may predate the start).
    const f = attachFeed()
    if (f !== null) onLocalView(f)
    ui.setState({ screen: 'station', tab: 'market', confirm: null, lastError: null, deathReport: null, notices: [] })
    publish()
    deps.begin()
  }

  async function startNewCareer(): Promise<void> {
    const c = newCareer(data, randomSeed() >>> 0)
    const reach = freshReachState(defaultGalaxySeed())
    restore(buildRecord({ career: c, worlds: { [systemId]: emptyWorldDeltas() }, reach, now: wallClock() }))
    if (deps.reach !== undefined) await deps.reach.enter(null, career)
    enterCareer()
    deps.reach?.entered()
    void autosave()
  }

  async function autosave(): Promise<CmdOutcome> {
    if (!tickState.active) return refusal('notReady', CAREER_TEXTS.notActive)
    const record = buildSave()
    tickState.nextAutosaveAt = mirror.gameTime + saveCfg.autosaveSeconds
    try {
      const r = await (await storage()).autosave(record)
      if (!r.ok) {
        emitCareer(CAREER_NOTICE.saveFailed, 0)
        notify('error', r.error.message)
        return { ok: false, error: r.error }
      }
      emitCareer(CAREER_NOTICE.saved, 0)
      await refreshSaves()
      return OK
    } catch (err) {
      logger.error('career: autosave failed', err)
      emitCareer(CAREER_NOTICE.saveFailed, 0)
      return refusal('storageError', errorMessage(err))
    }
  }

  /** Loads a record and starts it docked. */
  async function loadRecord(record: SaveRecord): Promise<CmdOutcome> {
    restore(record)
    if (deps.reach !== undefined) await deps.reach.enter(record.reach, career, record.savedAt)
    enterCareer()
    deps.reach?.entered()
    return OK
  }

  /**
   * A session action (storage, and a load when `loads`). None starts while a load runs; a loading one holds the
   * station UI and the game loop (`loading`, `HOLD.loading`) from its first storage read until it has docked.
   */
  async function sessionAction(run: () => Promise<CmdOutcome>, loads = false): Promise<CmdOutcome> {
    if (loading) return refuse({ code: 'notReady', message: CAREER_TEXTS.loading })
    // A trade settling now would book onto the loaded career.
    if (loads && tradeInFlight) return refuse({ code: 'notReady', message: CAREER_TEXTS.tradeSettling })
    deps.begin()
    if (loads) setLoading(true)
    try {
      const out = await run()
      if (!out.ok) ui.setState({ lastError: out.error })
      else ui.setState({ lastError: null })
      return out
    } catch (err) {
      logger.error('career: session action failed', err)
      return refuse({ code: 'storageError', message: errorMessage(err) })
    } finally {
      if (loads) setLoading(false)
    }
  }

  function setLoading(on: boolean): void {
    loading = on
    if (on) holds.add(HOLD.loading)
    else holds.delete(HOLD.loading)
    ui.setState({ loading: on })
  }

  async function importInto(text: string, slot: ManualSlotId, overwriteBad: boolean): Promise<CmdOutcome> {
    const slots = await storage()
    const r = await importSaveCmd(slots, text, slot, { overwriteBad })
    if (!r.ok) {
      if (r.error.code === 'corrupt' && !overwriteBad) {
        confirm({ kind: 'import', text, slot }, confirmTexts(CAREER_TEXTS.confirmBad, slot))
        return OK
      }
      return { ok: false, error: r.error }
    }
    await refreshSaves()
    // From the title, an imported save is played at once; in a career it only fills the slot.
    if (!tickState.active) {
      const loaded = await slots.load(slot)
      if (!loaded.ok) return { ok: false, error: loaded.error }
      return loadRecord(loaded.value)
    }
    notify('success', fillText(CAREER_TEXTS.imported, { slot: slotLabel(slot) }))
    return OK
  }

  async function saveInto(slot: ManualSlotId, record: SaveRecord, overwriteBad: boolean): Promise<CmdOutcome> {
    const r = await (await storage()).saveManual(slot, record, { overwriteBad })
    if (!r.ok) {
      if (r.error.code === 'corrupt' && !overwriteBad) {
        confirm({ kind: 'save', slot, record }, confirmTexts(CAREER_TEXTS.confirmBad, slot))
        return OK
      }
      emitCareer(CAREER_NOTICE.saveFailed, 0)
      return { ok: false, error: r.error }
    }
    emitCareer(CAREER_NOTICE.saved, 0)
    notify('success', fillText(CAREER_TEXTS.saved, { slot: slotLabel(slot) }))
    await refreshSaves()
    return OK
  }

  /** The oldest valid manual slot (else the first), for an import when every manual slot is used. */
  function oldestManual(list: readonly SaveSlotInfo[]): ManualSlotId {
    let best: SaveSlotInfo | null = null
    for (const s of list) {
      if (!(MANUAL_SLOTS as readonly string[]).includes(s.slot) || s.status !== 'ok') continue
      if (best === null || (s.savedAt ?? '') < (best.savedAt ?? '')) best = s
    }
    return (best?.slot as ManualSlotId | undefined) ?? MANUAL_SLOTS[0]
  }

  // ---- The UI actions ------------------------------------------------------------------------

  const actions: UiActions = {
    newCareer: () =>
      sessionAction(async () => {
        const list = await refreshSaves()
        if (list.some((s) => s.status !== 'empty')) {
          const t = CAREER_TEXTS.confirmNew
          confirm({ kind: 'new' }, { title: t.title, message: t.message, confirmLabel: t.confirmLabel, cancelLabel: t.cancelLabel })
          return OK
        }
        await startNewCareer()
        return OK
      }, true),

    continueCareer: () =>
      sessionAction(async () => {
        const best = await (await storage()).newestValid()
        if (best === null) return refusal('emptySlot', CAREER_TEXTS.noSave)
        return loadRecord(best.record)
      }, true),

    loadSlot: (slot: SaveSlotId) =>
      sessionAction(async () => {
        const r = await (await storage()).load(slot)
        if (!r.ok) return { ok: false, error: r.error }
        return loadRecord(r.value)
      }, true),

    importSave: (text: string, slot?: ManualSlotId) =>
      sessionAction(async () => {
        if (slot !== undefined) return importInto(text, slot, false)
        const list = await refreshSaves()
        const empty = MANUAL_SLOTS.slice(0, saveCfg.manualSlots).find((s) => list.find((x) => x.slot === s)?.status === 'empty')
        if (empty !== undefined) return importInto(text, empty, false)
        const target = oldestManual(list)
        const bad = list.find((x) => x.slot === target)?.status !== 'ok'
        confirm({ kind: 'import', text, slot: target }, confirmTexts(bad ? CAREER_TEXTS.confirmBad : CAREER_TEXTS.confirmOverwrite, target))
        return OK
        // From the title an import is played at once (a load).
      }, !tickState.active),

    async exportSave(slot: SaveSlotId): Promise<SaveExport | null> {
      return exportSlot(await storage(), slot, wallClock())
    },

    saveTo: (slot: ManualSlotId) =>
      sessionAction(async () => {
        const inactive = requireActive()
        if (inactive) return { ok: false, error: inactive }
        return saveInto(slot, buildSave(), false)
      }),

    answerConfirm: (id: string, accept: boolean) =>
      sessionAction(async () => {
        const current = pending
        pending = null
        ui.setState({ confirm: null })
        if (!accept || current === null || current.id !== id) return OK
        const a = current.action
        switch (a.kind) {
          case 'new':
            await startNewCareer()
            return OK
          case 'import':
            return importInto(a.text, a.slot, true)
          case 'save':
            return saveInto(a.slot, a.record, true)
        }
        // A new career, or an import from the title, is a load.
      }, accept && pending !== null && pending.id === id && pending.action.kind !== 'save'),

    setTab(tab: StationTab): CmdOutcome {
      // Mid-load the tab would be reset to Market when the career docks: the choice is ignored.
      if (loading) return OK
      ui.setState({ tab, lastError: null })
      return OK
    },

    sellWare: (ware: WareId, tonnes: number) =>
      atStation(undefined, (station, c) => {
        if (tenderGate(tenderCfg, mooredNow())) return queueTenderTrade(station, c, ware, tonnes, 'sell')
        const m = strategicAt(station)
        return m === null ? uiTrade(sellWareCmd(c, data, market, station.id, ware, tonnes)) : strategicTrade(m, station, c, ware, tonnes, 'sell')
      }),
    buyWare: (ware: WareId, tonnes: number) =>
      atStation(undefined, (station, c) => {
        if (tenderGate(tenderCfg, mooredNow())) return queueTenderTrade(station, c, ware, tonnes, 'buy')
        const m = strategicAt(station)
        return m === null ? uiTrade(buyWareCmd(c, data, market, station.id, ware, tonnes)) : strategicTrade(m, station, c, ware, tonnes, 'buy')
      }),
    sellComponent: (id: string) =>
      atStation(undefined, (station, c) => {
        const m = strategicAt(station)
        const component = m === null ? undefined : c.cargo.components.find((x) => x.id === id)
        const out = uiTrade(sellComponentCmd(c, data, market, station.id, id))
        if (out.ok && m !== null && component !== undefined) m.reportComponentSold(station.id, component.type, component.condition)
        return out
      }),
    sellAllCargo: () =>
      atStation(undefined, (station, c) => {
        const m = strategicAt(station)
        return m === null ? uiTrade(sellAllCargoCmd(c, data, market, station.id)) : strategicSellAll(m, station, c)
      }),
    // M10 P5a8a: sends the queued tender lines as one run (moored, the key on); else refused.
    sendTender: () =>
      atStation(undefined, () => {
        if (!tenderGate(tenderCfg, mooredNow())) return refuse({ code: 'serviceUnavailable', message: CAREER_TEXTS.tenderUnavailable })
        if (!sendTenderRun(tender, tenderCfg)) return refuse({ code: 'notReady', message: CAREER_TEXTS.tenderBusy })
        startTenderClock()
        publishTender()
        return done()
      }),
    buyGood: (good: GoodId, qty: number) => atStation(undefined, (station) => uiGoodTrade(station, good, qty, 'buy')),
    sellGood: (good: GoodId, qty: number) => atStation(undefined, (station) => uiGoodTrade(station, good, qty, 'sell')),
    quoteGood(good: GoodId, qty: number, side: 'buy' | 'sell'): MarketQuote | null {
      if (!tickState.active || tickState.docked < 0) return null
      return quoteGoodAt(stationsNow()[tickState.docked].id, good, qty, side)
    },
    // CL-3: the read-only quote at another station of this system (the Market's best sale); no worker command.
    quoteGoodAt: (stationId: string, good: GoodId, qty: number, side: 'buy' | 'sell'): MarketQuote | null =>
      tickState.active ? quoteGoodAt(stationId, good, qty, side) : null,
    // CL-3 (d): the docked station's ore quote, as the Market's ore rows price it (their after-trade line); no command.
    quoteWare(ware: WareId, qty: number, side: 'buy' | 'sell'): Quote | null {
      if (!tickState.active || tickState.docked < 0) return null
      const station = stationsNow()[tickState.docked]
      const c = folded()
      return quote(data, market, station.id, { kind: 'ware', ware }, qty, side, c.gameTime, c.standings[station.faction])
    },
    // SY-3: goods into the hold only; never `reportComponentSold` (nothing reaches the station's market).
    breakDown: (id: string) => atStation(undefined, (station, c) => uiCareer(breakDownComponent(c, data, station, id))),
    // SY-5b: goods from the hold only, no credits; the command checks the outfitting service itself.
    recondition: (id: string) => atStation(undefined, (station, c) => uiCareer(reconditionComponent(c, data, station, id))),
    // SY-7: a generic core for standing at the delivery faction's market station; no credits, no goods, no market report.
    deliverCore: (id: string) =>
      atStation(undefined, (station, c) => uiCareer(deliverCoreCmd(c, data, station, id, deps.reach?.region?.(data.stations.systemId) ?? null))),

    // MC-c6: ready-made Mk III only in a fabworks or yards system, read from the live local view (none: any station).
    buyModule: (id: ModuleId) => atStation(undefined, (station, c) => uiCareer(buyModuleCmd(c, data, station, id, geographyView()))),
    sellModule: (id: ModuleId) => atStation(undefined, (station, c) => uiCareer(sellModuleCmd(c, data, station, id))),
    fitModule: (slot: SlotId, id: ModuleId) => atStation(undefined, (station, c) => uiCareer(fitModuleCmd(c, data, station, slot, id))),
    // SY-5c over SY-5a: no goods are involved, so none of the three is gated by the goods flag; the commands check
    // the outfitting service. A salvaged sale reaches no station market (component value, D8).
    installComponent: (id: string, weaponKind?: ModuleKind) =>
      atStation(undefined, (station, c) => uiCareer(installComponentCmd(c, data, station, id, weaponKind))),
    fitSalvaged: (slot: SlotId, sourceId: string) => atStation(undefined, (station, c) => uiCareer(fitSalvagedCmd(c, data, station, slot, sourceId))),
    sellSalvaged: (sourceId: string) => atStation(undefined, (station, c) => uiCareer(sellSalvagedCmd(c, data, station, sourceId))),
    // M8 MC-0b: every lane-K career command goes through the craft hook's registered handlers.
    craft: craftCommand,

    // M8 SNK-3b: a repair under the strategic economy is paid in hull plating (hold first, then a station draw).
    repair: (points: number | 'all') =>
      atStation('repair', (station, c) => runService(station, c, planRepair(c, data, points, { def: sim.defs[ship.defIndex], linked: strategicAt(station) !== null }))),
    rearm: (count: number | 'all') =>
      atStation('repair', (station, c) => runService(station, c, planRearm(c, data, count, { def: sim.defs[ship.defIndex], linked: strategicAt(station) !== null }))),

    payArrears: () => atStation('lease', (_station, c) => uiCareer(payArrearsCmd(c, data))),
    payExtra: (amount: number) => atStation('lease', (_station, c) => uiCareer(payExtraCmd(c, data, amount))),
    setInsurance: (on: boolean) => atStation('lease', (_station, c) => uiCareer(setInsuranceCmd(c, on))),

    acceptContract: (id: string) =>
      atStation(undefined, (station, c) => {
        const out = uiCareer(acceptContractCmd(data, c, station.id, id))
        if (out.ok) spawnContractTarget(id)
        return out
      }),
    abandonContract(id: string): CmdOutcome {
      const inactive = requireActive()
      if (inactive) return refuse(inactive)
      return uiCareer(abandonContractCmd(data, folded(), id))
    },
    handInContract: (id: string) =>
      atStation(undefined, (station, c) => {
        // M8 SNK-6b2: a works delivery is paid by the site owner's treasury through the worker.
        const contract = c.contracts.active.find((k) => k.id === id)
        if (contract !== undefined && isWorksContract(contract) && contract.station === station.id) return handInWorks(station, c, contract)
        return uiCareer(handInContractCmd(data, c, station.id, id))
      }),

    launch(): CmdOutcome {
      const station = dockedAt()
      if ('code' in station) return refuse(station)
      // M8 SNK-3b: never undock while a trade or a service draw settles (its booking needs the docked ship).
      if (tradeInFlight) return refuse({ code: 'notReady', message: CAREER_TEXTS.tradeSettling })
      if (!assetsReady) return refuse({ code: 'notReady', message: CAREER_TEXTS.assetsLoading })
      const index = tickState.docked
      shipToSim()
      space.docking.launch(index)
      tickState.docked = -1
      // M10 P5a8a: leaving the ring cancels the unsent tender lines; a sent run keeps flying.
      if (tenderCfg.enabled) {
        leaveRing(tender)
        publishTender()
      }
      watch = null
      // Inside the Launch gesture, so the loop's pointer-lock request counts as one.
      holds.delete(HOLD.docked)
      space.encounters.notifyUndock()
      ui.setState({ screen: 'none', deathReport: null })
      const out = done()
      void autosave()
      return out
    },

    dismissNotice(id: number): CmdOutcome {
      ui.setState({ notices: ui.getState().notices.filter((n) => n.id !== id) })
      return OK
    },

    dismissScreen(): CmdOutcome {
      if (loading) return OK
      const screen = ui.getState().screen
      if (screen !== 'death' && screen !== 'ownership') return OK
      holds.delete(HOLD.moment)
      ui.setState({ screen: tickState.docked >= 0 ? 'station' : 'none', deathReport: null })
      return OK
    },
  }

  /** A recovery job's wreck or a clear-raiders job's group, placed at the contract marker. */
  function spawnContractTarget(id: string): void {
    const c = career.contracts.active.find((k) => k.id === id)
    const marker = c?.params.marker
    if (c === undefined || marker === undefined) return
    if (c.type === 'recovery' && c.params.componentType !== undefined) space.salvage.spawnContractWreck(id, marker, c.params.componentType)
    else if (c.type === 'clearRaiders') space.encounters.spawnContractGroup(id, marker, c.params.tier ?? 0)
  }

  const ui = createUiStore(actions)
  // The start fit's derived def and weapons, before any career (the title screen flies nothing).
  syncMirror()
  const ready = refreshSaves().then(
    () => undefined,
    (err: unknown) => logger.error('career: save storage failed to open', err),
  )

  const runtime: CareerRuntime = {
    ui,
    actions,
    career: () => career,
    market: () => market,
    get active(): boolean {
      return tickState.active
    },
    get docked(): number {
      return tickState.docked
    },
    get dirty(): boolean {
      return dirty
    },
    ready,
    tickState,
    tick: () => careerTick(tickState),
    publish,
    showTitle(): void {
      holds.add(HOLD.title)
      ui.setState({ screen: 'title' })
    },
    worldDeltas,
    buildSave,
    restore,
    enterCareer,
    autosave,
    setAssets(progress: number, isReady: boolean): void {
      assetsReady = isReady
      ui.setState({ assetsProgress: progress, assetsReady: isReady })
    },
    notify,
    applyDevCommand(name: string, cmd: DevCommand): CmdOutcome {
      const inactive = requireActive()
      if (inactive) return { ok: false, error: inactive }
      try {
        const before = folded()
        const next = cmd(before)
        // M8 SNK-3b: a ship switch (`boardShip`, the fleet service's write) waits for a settling trade or service
        // draw; the fleet service undoes its worker side on the refusal.
        if (tradeInFlight && (next.flownShip !== before.flownShip || next.ship.defId !== before.ship.defId)) {
          return refusal('notReady', CAREER_TEXTS.tradeSettling)
        }
        commit(next, [])
        // M10 P3c2: boarding while docked re-poses at the docked station by the new def's band (an L at the mooring
        // ring, an S or M on the pad). Here in the write path, not in `syncMirror`, which also runs in `restore` with
        // the previous session's docked index. `dockAt` emits no event, so the docked holds and screen stay.
        if (next.ship.defId !== before.ship.defId && tickState.docked >= 0) space.docking.dockAt(tickState.docked)
      } catch (err) {
        logger.warn(`career: dev command ${name} failed`, err)
        return refusal('invalidAmount', `${name}: ${errorMessage(err)}`)
      }
      publish()
      return OK
    },
    quoteGood: quoteGoodAt,
    buyGood: (stationId: string, good: GoodId, qty: number) => runtimeGoodTrade(stationId, good, qty, 'buy'),
    sellGood: (stationId: string, good: GoodId, qty: number) => runtimeGoodTrade(stationId, good, qty, 'sell'),
  }
  return runtime
}
